import React, { useEffect, useState, useCallback, useRef, useMemo, useId } from "react";
import { createPortal } from "react-dom";
import API from "../../Api/Api";
import { ENDPOINTS } from "../../Api/Endpoints";
import { getSocket } from "../../Api/socket";
import { SkeletonBlock, SkeletonText } from "../Skeleton/Skeleton";
import "./BidHistoryChat.scss";
import {
  appointmentClinicLabel,
  appointmentTimePart,
  parseTimeSlotDisplay,
  slotClinicLabel,
  slotTimePart,
} from "../../utils/timeSlotDisplay";
import ChatDeliveryTicks, { getOutgoingReceiptStatus } from "../ChatDeliveryTicks/ChatDeliveryTicks";
import {
  BID_AMOUNT_CONTACT_BLOCKED_MSG,
  bidAmountDigitsOnly,
  bidAmountInputToNumber,
  bidAmountRawHasDisallowedSymbols,
  formatIndianBidAmountInput,
  validateBidAmountDigitsNotPhoneLike,
} from "../../utils/bidAmountInput";
import { visitOutcomePatientDetail } from "../../Pages/PatientApp/PatientChatShared";
import { showAlert } from "../../feedback/feedbackApi";
import {
  clearActiveChatConversationId,
  setActiveChatConversationId,
} from "../../utils/activeChatConversation";

const CONVERSATION_REQUEST_TIMEOUT = 45000;
/** Match `BidHistoryChat.scss` — compact / full-screen chat layout */
const BHC_COMPACT_MEDIA = "(max-width: 768px)";

/** Prefer API `message` over Axios generic "Request failed with status code …". */
function apiErrorMessage(error, fallback) {
  const d = error?.response?.data;
  if (d && typeof d.message === "string" && d.message.trim()) return d.message.trim();
  if (d && typeof d.error === "string" && d.error.trim()) return d.error.trim();
  const m = error?.message;
  if (typeof m === "string" && m.trim()) {
    if (/^Request failed with status code \d+$/i.test(m.trim())) return fallback;
    return m.trim();
  }
  return fallback;
}

const TIME_SLOTS = [
  "9:00 AM",
  "9:30 AM",
  "10:00 AM",
  "10:30 AM",
  "11:00 AM",
  "11:30 AM",
  "12:00 PM",
  "12:30 PM",
  "1:00 PM",
  "1:30 PM",
  "2:00 PM",
  "2:30 PM",
  "3:00 PM",
  "3:30 PM",
  "4:00 PM",
  "4:30 PM",
  "5:00 PM",
  "5:30 PM",
  "6:00 PM",
  "6:30 PM",
];

/** `YYYY-MM-DD` for `<input type="date" />` from API date or ISO string. */
function appointmentDateForInput(appointmentDate) {
  if (!appointmentDate) return "";
  const s = String(appointmentDate).trim();
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  const d = new Date(s);
  if (!Number.isFinite(d.getTime())) return "";
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${day}`;
}

/** Include the patient’s booked time in the dropdown when it isn’t in the standard list. */
function mergeTimeSlotOptions(extraTimePart) {
  const raw = String(extraTimePart || "").trim();
  if (!raw) return [...TIME_SLOTS];
  const norm = (t) => String(t || "").trim().replace(/\s+/g, " ");
  const normalizedExtra = norm(raw);
  const seen = new Set(TIME_SLOTS.map(norm));
  const out = [];
  if (!seen.has(normalizedExtra)) {
    out.push(raw);
    seen.add(normalizedExtra);
  }
  for (const t of TIME_SLOTS) out.push(t);
  return out;
}

const fmtINR = (n) => `Rs ${Number(n).toLocaleString("en-IN")}`;

function formatBookingDateLabel(ymd) {
  if (!ymd) return "";
  const d = new Date(`${ymd}T12:00:00`);
  if (!Number.isFinite(d.getTime())) return ymd;
  return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
}

function formatChatTime(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  if (!Number.isFinite(d.getTime())) return "";
  return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
}

/** WhatsApp-style “last seen” line from an ISO date string. */
function formatLastSeen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "";
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const time = d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  if (sameDay) return `Last seen today at ${time}`;
  if (isYesterday) return `Last seen yesterday at ${time}`;
  return `Last seen ${d.toLocaleDateString("en-IN", { day: "numeric", month: "short" })} at ${time}`;
}

function isOutgoingMessage(m, mode) {
  if (m.messageType === "system") return false;
  if (mode === "patient") return m.senderRole === "user";
  return m.senderRole === "doctor" || m.senderRole === "hospital";
}

function getSystemMessageText(msg, mode) {
  const p = msg.payload || {};
  const type = p.type;
  const isPatientMode = mode === "patient";
  const providerLabel = mode === "hospital" ? "hospital" : "doctor";

  if (type === "initial_bid") {
    return `${isPatientMode ? "Your budget" : "Patient"}: ${fmtINR(p.amount)}`;
  }

  if (type === "reverse_bid") {
    return `${isPatientMode ? providerLabel.charAt(0).toUpperCase() + providerLabel.slice(1) : "Your"} suggested amount: ${fmtINR(p.amount)}`;
  }

  if (type === "patient_accept") {
    return isPatientMode
      ? `You accepted ${p.amount != null ? fmtINR(p.amount) : "the offer"}.`
      : "Patient accepted your offer.";
  }

  if (type === "patient_reject") {
    return isPatientMode ? "You rejected the offer." : "Patient rejected your offer.";
  }

  if (type === "patient_counter") {
    return isPatientMode ? `Your counter: ${fmtINR(p.amount)}` : `Patient countered: ${fmtINR(p.amount)}`;
  }

  if (type === "provider_accept") {
    return isPatientMode ? "Accepted your budget." : "You accepted the patient's bid.";
  }

  if (type === "provider_reject") {
    return isPatientMode ? "Declined your budget." : "You rejected the patient's bid.";
  }

  if (type === "appointment_requested") {
    const label = isPatientMode ? "Appointment requested" : "Patient requested appointment";
    const notePart = p.note ? ` — Note: ${p.note}` : "";
    const { timePart, clinicLabel } = parseTimeSlotDisplay(p.timeSlot || "");
    const loc = String(p.clinicName || "").trim() || clinicLabel;
    const when = timePart || p.timeSlot || "";
    const locPart = loc ? ` · ${loc}` : "";
    return `${label}: ${when}${p.date ? ` on ${new Date(p.date).toLocaleDateString()}` : ""}${locPart}${notePart}`;
  }

  if (type === "patient_booking_updated") {
    const label = isPatientMode ? "Visit time updated" : "Patient changed requested time";
    const { timePart, clinicLabel } = parseTimeSlotDisplay(p.timeSlot || "");
    const loc = String(p.clinicName || "").trim() || clinicLabel;
    const when = timePart || p.timeSlot || "";
    const locPart = loc ? ` · ${loc}` : "";
    return `${label}: ${when}${p.date ? ` on ${new Date(p.date).toLocaleDateString()}` : ""}${locPart}`;
  }

  if (type === "doctor_accept_appointment") {
    const acceptedLabel = isPatientMode
      ? "Appointment pending confirmation."
      : "You accepted the appointment. Pending admin approval.";
    return p.message ? `${acceptedLabel} Message: ${p.message}` : acceptedLabel;
  }

  if (type === "doctor_reschedule") {
    const base = isPatientMode
      ? "Appointment time update pending confirmation"
      : "You proposed a new appointment time. Pending confirmation";
    return `${base}: ${p.proposedTimeSlot || ""}${p.proposedDate ? ` on ${new Date(p.proposedDate).toLocaleDateString()}` : ""}`;
  }

  if (type === "admin_reschedule") {
    const base = isPatientMode
      ? "HealBid admin selected a new time — pending final confirmation"
      : "HealBid admin proposed a new time from published availability — pending confirmation";
    return `${base}: ${p.proposedTimeSlot || ""}${p.proposedDate ? ` on ${new Date(p.proposedDate).toLocaleDateString()}` : ""}`;
  }

  if (type === "appointment_approved") {
    const { timePart, clinicLabel } = parseTimeSlotDisplay(p.timeSlot || "");
    const loc = String(p.clinicName || "").trim() || clinicLabel;
    const when = timePart || p.timeSlot || "";
    const locPart = loc ? ` · ${loc}` : "";
    return `Appointment confirmed: ${when}${p.date ? ` on ${new Date(p.date).toLocaleDateString()}` : ""}${locPart}`;
  }

  if (type === "slot_held") {
    const { timePart } = parseTimeSlotDisplay(p.timeSlot || "");
    const when = timePart || p.timeSlot || "";
    const dateStr = p.appointmentDate ? new Date(`${p.appointmentDate}T12:00:00`).toLocaleDateString() : "";
    if (isPatientMode) {
      return dateStr || when
        ? `Visit time (reference) — ${[when, dateStr].filter(Boolean).join(" · ")}. Choose a slot and tap Book this visit.`
        : "Choose a slot and tap Book this visit to send your request.";
    }
    return dateStr || when
      ? `Patient noted a time (reference) — ${[when, dateStr].filter(Boolean).join(" · ")}.`
      : "Patient noted a visit time (reference).";
  }

  return msg.text || type || "Update";
}

const LEDGER_PRICE_TYPES = new Set(["initial_bid", "reverse_bid", "patient_counter"]);

function isLedgerPriceSystemMessage(m) {
  return (
    m?.messageType === "system" && LEDGER_PRICE_TYPES.has(String(m?.payload?.type || ""))
  );
}

/** Full-width timeline row for money moves (replaces weak grey chat bubble). */
function BidLedgerSystemMessage({ m, mode }) {
  const p = m?.payload || {};
  const t = p.type;
  const isPatientMode = mode === "patient";
  const providerLabel = mode === "hospital" ? "Hospital" : "Doctor";
  const amount = p.amount;
  const hasAmount = amount != null && Number.isFinite(Number(amount));

  let side = "patient";
  let title = "";
  if (t === "initial_bid") {
    side = "patient";
    title = isPatientMode ? "Your budget" : "Patient opened with";
  } else if (t === "reverse_bid") {
    side = "provider";
    title = isPatientMode ? `${providerLabel} suggested another amount` : "You counter-offered";
  } else if (t === "patient_counter") {
    side = "patient";
    title = isPatientMode ? "You counter-offered" : "Patient counter-offered";
  } else {
    return null;
  }

  return (
    <div className={`bhc-ledger-event bhc-ledger-event--${side}`} role="status">
      <div className="bhc-ledger-event__body">
        <span className="bhc-ledger-event__title">{title}</span>
        {hasAmount ? (
          <span className="bhc-ledger-event__amount">{fmtINR(Number(amount))}</span>
        ) : (
          <span className="bhc-ledger-event__amount bhc-ledger-event__amount--muted">—</span>
        )}
      </div>
      <span className="bhc-ledger-event__time">{formatChatTime(m.createdAt)}</span>
    </div>
  );
}

const APPOINTMENT_CARD_SYSTEM_TYPES = new Set([
  "appointment_requested",
  "patient_booking_updated",
  "doctor_accept_appointment",
  "doctor_reschedule",
  "admin_reschedule",
  "appointment_approved",
  "appointment_completed",
  "appointment_visit_outcome",
  "slot_held",
]);

function isAppointmentSystemCard(m) {
  return (
    m?.messageType === "system" && APPOINTMENT_CARD_SYSTEM_TYPES.has(String(m?.payload?.type || ""))
  );
}

function formatApptDisplayDate(input) {
  if (input == null || input === "") return "";
  try {
    const s = String(input).trim();
    const d = /^\d{4}-\d{2}-\d{2}/.test(s) ? new Date(`${s.slice(0, 10)}T12:00:00`) : new Date(s);
    if (!Number.isFinite(d.getTime())) return "";
    return d.toLocaleDateString("en-IN", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

function ApptIconClock() {
  return (
    <svg className="bhc-appt-card__svg" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.75" />
      <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ApptIconCalendar() {
  return (
    <svg className="bhc-appt-card__svg" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.75" />
      <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

function ApptIconMapPin() {
  return (
    <svg className="bhc-appt-card__svg" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 21c-4-3.2-6-6.4-6-9.5a6 6 0 1 1 12 0c0 3.1-2 6.3-6 9.5z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="11" r="2.25" stroke="currentColor" strokeWidth="1.75" />
    </svg>
  );
}

function ApptIconCheck() {
  return (
    <svg className="bhc-appt-card__svg" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M5 13l4 4L19 7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Rich layout for visit / appointment system events (replaces plain grey bubbles). */
function AppointmentSystemMessage({ m, mode }) {
  const p = m?.payload || {};
  const t = p.type;
  const isPatientMode = mode === "patient";
  const providerLabel = mode === "hospital" ? "Hospital" : "Doctor";
  const ts = formatChatTime(m.createdAt);

  if (t === "appointment_requested") {
    const { timePart, clinicLabel } = parseTimeSlotDisplay(p.timeSlot || "");
    const loc = String(p.clinicName || "").trim() || clinicLabel;
    const when = timePart || String(p.timeSlot || "").trim();
    const dateLabel = formatApptDisplayDate(p.date);
    const note = String(p.note || "").trim();

    return (
      <div className="bhc-appt-card bhc-appt-card--request" role="status">
        <div className="bhc-appt-card__top">
          <div className="bhc-appt-card__titles">
            <span className="bhc-appt-card__eyebrow">{isPatientMode ? "Your request" : "Patient"}</span>
            <span className="bhc-appt-card__headline">
              {isPatientMode ? "Appointment requested" : "Requested a visit"}
            </span>
          </div>
          <span className="bhc-appt-card__stamp">{ts}</span>
        </div>
        <div className="bhc-appt-card__grid">
          {when ? (
            <div className="bhc-appt-card__cell">
              <span className="bhc-appt-card__cell-icon" aria-hidden>
                <ApptIconClock />
              </span>
              <div className="bhc-appt-card__cell-body">
                <span className="bhc-appt-card__cell-label">Time</span>
                <span className="bhc-appt-card__cell-strong">{when}</span>
              </div>
            </div>
          ) : null}
          {dateLabel ? (
            <div className="bhc-appt-card__cell">
              <span className="bhc-appt-card__cell-icon" aria-hidden>
                <ApptIconCalendar />
              </span>
              <div className="bhc-appt-card__cell-body">
                <span className="bhc-appt-card__cell-label">Date</span>
                <span className="bhc-appt-card__cell-strong">{dateLabel}</span>
              </div>
            </div>
          ) : null}
          {loc ? (
            <div className="bhc-appt-card__cell bhc-appt-card__cell--wide">
              <span className="bhc-appt-card__cell-icon bhc-appt-card__cell-icon--pin" aria-hidden>
                <ApptIconMapPin />
              </span>
              <div className="bhc-appt-card__cell-body">
                <span className="bhc-appt-card__cell-label">Clinic / location</span>
                <span className="bhc-appt-card__cell-clinic">{loc}</span>
              </div>
            </div>
          ) : null}
        </div>
        {note ? (
          <p className="bhc-appt-card__note">
            <strong>Note:</strong> {note}
          </p>
        ) : null}
      </div>
    );
  }

  if (t === "patient_booking_updated") {
    const { timePart, clinicLabel } = parseTimeSlotDisplay(p.timeSlot || "");
    const loc = String(p.clinicName || "").trim() || clinicLabel;
    const when = timePart || String(p.timeSlot || "").trim();
    const dateLabel = formatApptDisplayDate(p.date);

    return (
      <div className="bhc-appt-card bhc-appt-card--request" role="status">
        <div className="bhc-appt-card__top">
          <div className="bhc-appt-card__titles">
            <span className="bhc-appt-card__eyebrow">{isPatientMode ? "Your update" : "Patient"}</span>
            <span className="bhc-appt-card__headline">
              {isPatientMode ? "Visit time updated" : "Patient changed the requested slot"}
            </span>
            <span className="bhc-appt-card__subline">
              {isPatientMode
                ? "Before your provider reviewed the request, you picked a new published time."
                : "They chose another slot from your published availability before you responded."}
            </span>
          </div>
          <span className="bhc-appt-card__stamp">{ts}</span>
        </div>
        <div className="bhc-appt-card__grid">
          {when ? (
            <div className="bhc-appt-card__cell">
              <span className="bhc-appt-card__cell-icon" aria-hidden>
                <ApptIconClock />
              </span>
              <div className="bhc-appt-card__cell-body">
                <span className="bhc-appt-card__cell-label">Time</span>
                <span className="bhc-appt-card__cell-strong">{when}</span>
              </div>
            </div>
          ) : null}
          {dateLabel ? (
            <div className="bhc-appt-card__cell">
              <span className="bhc-appt-card__cell-icon" aria-hidden>
                <ApptIconCalendar />
              </span>
              <div className="bhc-appt-card__cell-body">
                <span className="bhc-appt-card__cell-label">Date</span>
                <span className="bhc-appt-card__cell-strong">{dateLabel}</span>
              </div>
            </div>
          ) : null}
          {loc ? (
            <div className="bhc-appt-card__cell bhc-appt-card__cell--wide">
              <span className="bhc-appt-card__cell-icon bhc-appt-card__cell-icon--pin" aria-hidden>
                <ApptIconMapPin />
              </span>
              <div className="bhc-appt-card__cell-body">
                <span className="bhc-appt-card__cell-label">Clinic / location</span>
                <span className="bhc-appt-card__cell-clinic">{loc}</span>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  if (t === "doctor_reschedule") {
    const { timePart, clinicLabel } = parseTimeSlotDisplay(p.proposedTimeSlot || "");
    const loc = String(p.clinicName || "").trim() || clinicLabel;
    const when = timePart || String(p.proposedTimeSlot || "").trim();
    const dateLabel = formatApptDisplayDate(p.proposedDate);

    return (
      <div className="bhc-appt-card bhc-appt-card--provider-action" role="status">
        <div className="bhc-appt-card__top">
          <div className="bhc-appt-card__titles">
            <span className="bhc-appt-card__badge bhc-appt-card__badge--amber">Pending admin</span>
            <span className="bhc-appt-card__headline">
              {isPatientMode ? "New time proposed" : "You proposed a new time"}
            </span>
            <span className="bhc-appt-card__subline">
              {isPatientMode
                ? `${providerLabel} suggested a change. You will see the final slot after approval.`
                : "HealBid admin will confirm before the patient is notified."}
            </span>
          </div>
          <span className="bhc-appt-card__stamp">{ts}</span>
        </div>
        <div className="bhc-appt-card__grid">
          {when ? (
            <div className="bhc-appt-card__cell">
              <span className="bhc-appt-card__cell-icon" aria-hidden>
                <ApptIconClock />
              </span>
              <div className="bhc-appt-card__cell-body">
                <span className="bhc-appt-card__cell-label">Proposed time</span>
                <span className="bhc-appt-card__cell-strong">{when}</span>
              </div>
            </div>
          ) : null}
          {dateLabel ? (
            <div className="bhc-appt-card__cell">
              <span className="bhc-appt-card__cell-icon" aria-hidden>
                <ApptIconCalendar />
              </span>
              <div className="bhc-appt-card__cell-body">
                <span className="bhc-appt-card__cell-label">Proposed date</span>
                <span className="bhc-appt-card__cell-strong">{dateLabel}</span>
              </div>
            </div>
          ) : null}
          {loc ? (
            <div className="bhc-appt-card__cell bhc-appt-card__cell--wide">
              <span className="bhc-appt-card__cell-icon bhc-appt-card__cell-icon--pin" aria-hidden>
                <ApptIconMapPin />
              </span>
              <div className="bhc-appt-card__cell-body">
                <span className="bhc-appt-card__cell-label">Location</span>
                <span className="bhc-appt-card__cell-clinic">{loc}</span>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  if (t === "admin_reschedule") {
    const { timePart, clinicLabel } = parseTimeSlotDisplay(p.proposedTimeSlot || "");
    const loc = String(p.clinicName || "").trim() || clinicLabel;
    const when = timePart || String(p.proposedTimeSlot || "").trim();
    const dateLabel = formatApptDisplayDate(p.proposedDate);

    return (
      <div className="bhc-appt-card bhc-appt-card--reference" role="status">
        <div className="bhc-appt-card__top">
          <div className="bhc-appt-card__titles">
            <span className="bhc-appt-card__badge bhc-appt-card__badge--amber">Pending verify</span>
            <span className="bhc-appt-card__headline">
              {isPatientMode ? "Admin picked a new slot" : "HealBid admin proposed a slot"}
            </span>
            <span className="bhc-appt-card__subline">
              {isPatientMode
                ? "Chosen from published provider availability. You will see the final visit time after admin verification."
                : "From your published availability. Another admin step publishes this to the patient."}
            </span>
          </div>
          <span className="bhc-appt-card__stamp">{ts}</span>
        </div>
        <div className="bhc-appt-card__grid">
          {when ? (
            <div className="bhc-appt-card__cell">
              <span className="bhc-appt-card__cell-icon" aria-hidden>
                <ApptIconClock />
              </span>
              <div className="bhc-appt-card__cell-body">
                <span className="bhc-appt-card__cell-label">Proposed time</span>
                <span className="bhc-appt-card__cell-strong">{when}</span>
              </div>
            </div>
          ) : null}
          {dateLabel ? (
            <div className="bhc-appt-card__cell">
              <span className="bhc-appt-card__cell-icon" aria-hidden>
                <ApptIconCalendar />
              </span>
              <div className="bhc-appt-card__cell-body">
                <span className="bhc-appt-card__cell-label">Proposed date</span>
                <span className="bhc-appt-card__cell-strong">{dateLabel}</span>
              </div>
            </div>
          ) : null}
          {loc ? (
            <div className="bhc-appt-card__cell bhc-appt-card__cell--wide">
              <span className="bhc-appt-card__cell-icon bhc-appt-card__cell-icon--pin" aria-hidden>
                <ApptIconMapPin />
              </span>
              <div className="bhc-appt-card__cell-body">
                <span className="bhc-appt-card__cell-label">Clinic / location</span>
                <span className="bhc-appt-card__cell-clinic">{loc}</span>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  if (t === "doctor_accept_appointment") {
    const extra = String(p.message || "").trim();
    return (
      <div className="bhc-appt-card bhc-appt-card--accept" role="status">
        <div className="bhc-appt-card__top">
          <div className="bhc-appt-card__titles">
            <span className="bhc-appt-card__badge bhc-appt-card__badge--amber">Pending admin</span>
            <span className="bhc-appt-card__headline">
              {isPatientMode ? "Visit accepted" : "You accepted this visit"}
            </span>
            <span className="bhc-appt-card__subline">
              {isPatientMode
                ? `${providerLabel} accepted your slot. Final timing may update after admin review.`
                : "Admin will confirm before the patient sees this as final."}
            </span>
          </div>
          <span className="bhc-appt-card__stamp">{ts}</span>
        </div>
        {extra ? (
          <p className="bhc-appt-card__note">
            <strong>Message:</strong> {extra}
          </p>
        ) : null}
      </div>
    );
  }

  if (t === "appointment_approved") {
    const { timePart, clinicLabel } = parseTimeSlotDisplay(p.timeSlot || "");
    const loc = String(p.clinicName || "").trim() || clinicLabel;
    const when = timePart || String(p.timeSlot || "").trim();
    const dateLabel = formatApptDisplayDate(p.date);

    return (
      <div className="bhc-appt-card bhc-appt-card--confirmed-msg" role="status">
        <div className="bhc-appt-card__top">
          <div className="bhc-appt-card__titles">
            <span className="bhc-appt-card__badge bhc-appt-card__badge--success">
              <ApptIconCheck />
              <span>Confirmed</span>
            </span>
            <span className="bhc-appt-card__headline">Visit confirmed</span>
          </div>
          <span className="bhc-appt-card__stamp">{ts}</span>
        </div>
        <div className="bhc-appt-card__grid">
          {when ? (
            <div className="bhc-appt-card__cell">
              <span className="bhc-appt-card__cell-icon" aria-hidden>
                <ApptIconClock />
              </span>
              <div className="bhc-appt-card__cell-body">
                <span className="bhc-appt-card__cell-label">Time</span>
                <span className="bhc-appt-card__cell-strong">{when}</span>
              </div>
            </div>
          ) : null}
          {dateLabel ? (
            <div className="bhc-appt-card__cell">
              <span className="bhc-appt-card__cell-icon" aria-hidden>
                <ApptIconCalendar />
              </span>
              <div className="bhc-appt-card__cell-body">
                <span className="bhc-appt-card__cell-label">Date</span>
                <span className="bhc-appt-card__cell-strong">{dateLabel}</span>
              </div>
            </div>
          ) : null}
          {loc ? (
            <div className="bhc-appt-card__cell bhc-appt-card__cell--wide">
              <span className="bhc-appt-card__cell-icon bhc-appt-card__cell-icon--pin" aria-hidden>
                <ApptIconMapPin />
              </span>
              <div className="bhc-appt-card__cell-body">
                <span className="bhc-appt-card__cell-label">Clinic / location</span>
                <span className="bhc-appt-card__cell-clinic">{loc}</span>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  if (t === "appointment_completed") {
    return (
      <div className="bhc-appt-card bhc-appt-card--confirmed-msg" role="status">
        <div className="bhc-appt-card__top">
          <div className="bhc-appt-card__titles">
            <span className="bhc-appt-card__badge bhc-appt-card__badge--success">
              <ApptIconCheck />
              <span>Completed</span>
            </span>
            <span className="bhc-appt-card__headline">Visit completed</span>
            <span className="bhc-appt-card__subline">
              {isPatientMode
                ? "This visit is marked complete on HealBid."
                : "Visit marked complete for this patient on HealBid."}
            </span>
          </div>
          <span className="bhc-appt-card__stamp">{ts}</span>
        </div>
      </div>
    );
  }

  if (t === "appointment_visit_outcome") {
    const body = visitOutcomePatientDetail(p, providerLabel);
    const bookedOk = p.bookedPriceInr != null && Number.isFinite(Number(p.bookedPriceInr));
    const finalOk =
      p.finalAgreedPriceInr != null && Number.isFinite(Number(p.finalAgreedPriceInr));
    const summary =
      bookedOk &&
      p.patientAttended === true &&
      p.procedureProceeding === true &&
      p.samePrice === false &&
      finalOk
        ? `${fmtINR(Number(p.bookedPriceInr))} booked · ${fmtINR(Number(p.finalAgreedPriceInr))} logged`
        : bookedOk && p.patientAttended !== false
          ? `Booked quote ${fmtINR(Number(p.bookedPriceInr))}`
          : "";
    const warn = p.patientAttended === false || (p.patientAttended === true && p.procedureProceeding === false);
    const successVisit = p.patientAttended === true && p.procedureProceeding === true;

    return (
      <div className={`bhc-appt-card ${warn ? "bhc-appt-card--accept" : "bhc-appt-card--confirmed-msg"}`} role="status">
        <div className="bhc-appt-card__top">
          <div className="bhc-appt-card__titles">
            <span
              className={`bhc-appt-card__badge ${warn ? "bhc-appt-card__badge--amber" : "bhc-appt-card__badge--success"}`}
            >
              {warn ? (
                <span>Update</span>
              ) : (
                <>
                  <ApptIconCheck />
                  <span>{successVisit ? "Completed" : "Recorded"}</span>
                </>
              )}
            </span>
            <span className="bhc-appt-card__headline">{successVisit ? "Visit completed" : "Visit outcome"}</span>
            {summary ? <span className="bhc-appt-card__subline">{summary}</span> : null}
          </div>
          <span className="bhc-appt-card__stamp">{ts}</span>
        </div>
        <p className="bhc-appt-card__note">{body}</p>
      </div>
    );
  }

  if (t === "slot_held") {
    const { timePart } = parseTimeSlotDisplay(p.timeSlot || "");
    const when = timePart || String(p.timeSlot || "").trim();
    const dateStr = p.appointmentDate
      ? new Date(`${String(p.appointmentDate).slice(0, 10)}T12:00:00`).toLocaleDateString("en-IN", {
          weekday: "short",
          day: "numeric",
          month: "short",
        })
      : "";

    return (
      <div className="bhc-appt-card bhc-appt-card--reference" role="status">
        <div className="bhc-appt-card__top">
          <div className="bhc-appt-card__titles">
            <span className="bhc-appt-card__eyebrow">Reference</span>
            <span className="bhc-appt-card__headline">
              {isPatientMode ? "Preferred visit time" : "Patient noted a time"}
            </span>
          </div>
          <span className="bhc-appt-card__stamp">{ts}</span>
        </div>
        <p className="bhc-appt-card__reference-line">
          {[when, dateStr].filter(Boolean).join(" · ") || "Time preference on file."}
        </p>
        {isPatientMode ? (
          <p className="bhc-appt-card__subline bhc-appt-card__subline--tight">
            Choose an open slot below and tap <strong>Book visit</strong> to send your request.
          </p>
        ) : null}
      </div>
    );
  }

  return null;
}

const NEGOTIATION_OUTCOME_TYPES = new Set([
  "patient_accept",
  "provider_accept",
  "patient_reject",
  "provider_reject",
]);

function isNegotiationOutcomeCard(m) {
  return (
    m?.messageType === "system" && NEGOTIATION_OUTCOME_TYPES.has(String(m?.payload?.type || ""))
  );
}

function DealSealCheckIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M6 12.5L10.5 17L18 7"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function DealDeclineIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M8 8l8 8M16 8l-8 8"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Full-width “deal sealed” / decline cards for negotiation outcomes (replaces plain chat bubbles). */
function NegotiationOutcomeMessage({ m, mode }) {
  const p = m?.payload || {};
  const t = p.type;
  const isPatientMode = mode === "patient";
  const providerLabel = mode === "hospital" ? "Hospital" : "Doctor";
  const ts = formatChatTime(m.createdAt);
  const amt = p.amount;
  const hasAmt = amt != null && Number.isFinite(Number(amt));

  if (t === "patient_accept") {
    return (
      <div className="bhc-deal-card bhc-deal-card--agreed" role="status">
        <div className="bhc-deal-card__seal" aria-hidden>
          <DealSealCheckIcon />
        </div>
        <div className="bhc-deal-card__body">
          <span className="bhc-deal-card__badge">Offer agreed</span>
          <span className="bhc-deal-card__title">
            {isPatientMode ? "You accepted their latest offer" : "Patient accepted your offer"}
          </span>
          {hasAmt ? <span className="bhc-deal-card__amount">{fmtINR(Number(amt))}</span> : null}
          <span className="bhc-deal-card__hint">
            {isPatientMode
              ? `Next: when ${providerLabel} opens slots, book your visit from this chat.`
              : "Next: they can request a visit from their chat when you are ready to show availability."}
          </span>
        </div>
        <span className="bhc-deal-card__time">{ts}</span>
      </div>
    );
  }

  if (t === "provider_accept") {
    return (
      <div className="bhc-deal-card bhc-deal-card--matched" role="status">
        <div className="bhc-deal-card__seal bhc-deal-card__seal--teal" aria-hidden>
          <DealSealCheckIcon />
        </div>
        <div className="bhc-deal-card__body">
          <span className="bhc-deal-card__badge bhc-deal-card__badge--teal">Budget matched</span>
          <span className="bhc-deal-card__title">
            {isPatientMode ? `${providerLabel} accepted your bid` : "You accepted the patient’s bid"}
          </span>
          {hasAmt ? <span className="bhc-deal-card__amount bhc-deal-card__amount--teal">{fmtINR(Number(amt))}</span> : null}
          <span className="bhc-deal-card__hint">
            {isPatientMode
              ? "You can request a visit once they publish bookable times."
              : "They can request a visit from their side when you have slots to offer."}
          </span>
        </div>
        <span className="bhc-deal-card__time">{ts}</span>
      </div>
    );
  }

  if (t === "patient_reject") {
    return (
      <div className="bhc-deal-card bhc-deal-card--declined" role="status">
        <div className="bhc-deal-card__seal bhc-deal-card__seal--rose" aria-hidden>
          <DealDeclineIcon />
        </div>
        <div className="bhc-deal-card__body">
          <span className="bhc-deal-card__badge bhc-deal-card__badge--rose">Declined</span>
          <span className="bhc-deal-card__title">
            {isPatientMode ? "You declined this offer" : "Patient declined"}
          </span>
          <span className="bhc-deal-card__hint">
            {isPatientMode ? "No further action needed unless you want to send a new bid later." : "This thread is closed from their side on price."}
          </span>
        </div>
        <span className="bhc-deal-card__time">{ts}</span>
      </div>
    );
  }

  if (t === "provider_reject") {
    return (
      <div className="bhc-deal-card bhc-deal-card--declined" role="status">
        <div className="bhc-deal-card__seal bhc-deal-card__seal--rose" aria-hidden>
          <DealDeclineIcon />
        </div>
        <div className="bhc-deal-card__body">
          <span className="bhc-deal-card__badge bhc-deal-card__badge--rose">Declined</span>
          <span className="bhc-deal-card__title">
            {isPatientMode ? `${providerLabel} declined your bid` : "You declined this request"}
          </span>
          <span className="bhc-deal-card__hint">
            {isPatientMode ? "You can explore other providers or start a new request when ready." : "The patient is notified that this offer was not accepted."}
          </span>
        </div>
        <span className="bhc-deal-card__time">{ts}</span>
      </div>
    );
  }

  return null;
}

/** Admin direct messages may be targeted; ignore on this client if not for us. */
function shouldShowAdminMessageForViewer(raw, isPatientMode, mode) {
  if (String(raw?.senderRole) !== "admin" || String(raw?.messageType) !== "text") return true;
  const aud = String(raw.adminAudience || "both").toLowerCase();
  if (aud === "both") return true;
  if (aud === "patient") return isPatientMode;
  if (aud === "provider") return !isPatientMode && (mode === "doctor" || mode === "hospital");
  return true;
}

function getMessageSide(m) {
  if (m.messageType === "system" && m.payload?.type) {
    const t = m.payload.type;
    if (t === "admin_reschedule") return "system";
    const patientSide = [
      "initial_bid",
      "patient_accept",
      "patient_reject",
      "patient_counter",
      "appointment_requested",
      "patient_booking_updated",
      "slot_held",
    ];
    const providerSide = [
      "reverse_bid",
      "provider_accept",
      "provider_reject",
      "doctor_accept_appointment",
      "doctor_reschedule",
      "appointment_approved",
      "appointment_completed",
      "appointment_visit_outcome",
    ];
    if (patientSide.includes(t)) return "patient";
    if (providerSide.includes(t)) return "provider";
    return "system";
  }

  if (m.senderRole === "admin") return "system";
  if (m.senderRole === "user") return "patient";
  if (m.senderRole === "doctor" || m.senderRole === "hospital") return "provider";
  return "system";
}

export default function BidHistoryChat({
  open,
  onClose,
  mode,
  bidId,
  providerName,
  patientName,
  /** Presigned URL for the person you are chatting with (patient avatar on provider UIs, provider on patient UI). */
  peerAvatarUrl,
  initialBid,
  matchRequestId,
  onBidUpdated,
  requestId,
  dispatch,
  getMatchResults,
  getProviderCount,
  respondToBid,
  patientCounterBid,
  bookAppointment,
}) {
  const [conversationId, setConversationId] = useState(null);
  const [bid, setBid] = useState(initialBid || null);
  const [appointment, setAppointment] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [error, setError] = useState("");
  const [reverseBidAmount, setReverseBidAmount] = useState("");
  const [reverseBidSubmitting, setReverseBidSubmitting] = useState(false);
  const [reverseBidError, setReverseBidError] = useState("");
  const [showBooking, setShowBooking] = useState(false);
  const [bookingSelection, setBookingSelection] = useState(null);
  const [bookingSlots, setBookingSlots] = useState([]);
  const [bookingSlotsLoading, setBookingSlotsLoading] = useState(false);
  const [bookingSubmitting, setBookingSubmitting] = useState(false);
  const [showReverseBidInput, setShowReverseBidInput] = useState(false);
  const [appointmentRescheduleDate, setAppointmentRescheduleDate] = useState("");
  const [appointmentRescheduleSlot, setAppointmentRescheduleSlot] = useState("");
  /** null | "accept" | "reschedule" | "complete" */
  const [appointmentSaving, setAppointmentSaving] = useState(null);

  const [agreedPrice, setAgreedPrice] = useState(null);
  const [treatmentBill, setTreatmentBill] = useState(null);
  const [billFinalAmount, setBillFinalAmount] = useState("");
  const [billPaymentMethod, setBillPaymentMethod] = useState("cash");
  const [billChargedMore, setBillChargedMore] = useState(false);
  const [billReason, setBillReason] = useState("");
  const [billNotes, setBillNotes] = useState("");
  const [billSubmitting, setBillSubmitting] = useState(false);
  const [patientBillSubmitting, setPatientBillSubmitting] = useState(false);
  const [billUiError, setBillUiError] = useState("");
  const [patientBillDisputeNote, setPatientBillDisputeNote] = useState("");
  /** Provider treatment-bill form: collapsed by default so chat stays visible. */
  const [treatmentBillSectionOpen, setTreatmentBillSectionOpen] = useState(false);
  const [caseClose, setCaseClose] = useState(null);
  const [verifyCloseSubmitting, setVerifyCloseSubmitting] = useState(false);
  const treatmentBillPanelId = useId();
  const treatmentBillFormId = useId();
  const treatmentBillDisclosureRef = useRef(null);

  const [peerTyping, setPeerTyping] = useState(false);
  const [peerOnline, setPeerOnline] = useState(false);
  const [peerLastSeenAt, setPeerLastSeenAt] = useState(null);
  /** Facebook-style chat: docked bottom-right, header toggles expand/collapse. */
  const [collapsed, setCollapsed] = useState(false);
  /** Viewport ≤768px: expanded chat uses full-screen sheet (see BidHistoryChat.scss). */
  const [narrowViewport, setNarrowViewport] = useState(
    () => typeof window !== "undefined" && window.matchMedia(BHC_COMPACT_MEDIA).matches
  );

  const messagesEndRef = useRef(null);
  const typingStopTimerRef = useRef(null);

  const initialBidRef = useRef(initialBid);
  initialBidRef.current = initialBid;

  useEffect(() => {
    if (open) setCollapsed(false);
  }, [open]);

  /** Treatment bill sits after long appointment blocks — scroll it into the panel when expanded. */
  useEffect(() => {
    if (!treatmentBillSectionOpen) return;
    let cancelled = false;
    const scrollBillIntoView = () => {
      if (cancelled) return;
      treatmentBillDisclosureRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    };
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(scrollBillIntoView);
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(id);
    };
  }, [treatmentBillSectionOpen]);

  const isPatientMode = mode === "patient";
  const isHospitalMode = mode === "hospital";
  const isProviderMode = mode === "doctor" || mode === "hospital";

  const getConversationUrl = isPatientMode
    ? ENDPOINTS.GET_BID_CONVERSATION(bidId)
    : isHospitalMode
      ? ENDPOINTS.GET_HOSPITAL_BID_CONVERSATION(bidId)
      : ENDPOINTS.GET_DOCTOR_BID_CONVERSATION(bidId);

  const getProviderCounterUrl = useCallback(
    (id) => (isHospitalMode ? `/hospital/bids/${id}/counter` : ENDPOINTS.DOCTOR_BID_COUNTER(id)),
    [isHospitalMode]
  );

  const getProviderAcceptUrl = useCallback(
    (id) => (isHospitalMode ? `/hospital/bids/${id}/accept-patient-price` : ENDPOINTS.DOCTOR_BID_ACCEPT(id)),
    [isHospitalMode]
  );

  const getProviderRejectUrl = useCallback(
    (id) => (isHospitalMode ? `/hospital/bids/${id}/reject` : ENDPOINTS.DOCTOR_BID_REJECT(id)),
    [isHospitalMode]
  );

  const getProviderAppointmentRespondUrl = useCallback(
    (id) => (isHospitalMode ? `/hospital/appointments/${id}/respond` : ENDPOINTS.DOCTOR_APPOINTMENT_RESPOND(id)),
    [isHospitalMode]
  );
  const getProviderAppointmentCompleteUrl = useCallback(
    (id) =>
      isHospitalMode
        ? ENDPOINTS.HOSPITAL_APPOINTMENT_COMPLETE(id)
        : ENDPOINTS.DOCTOR_APPOINTMENT_COMPLETE(id),
    [isHospitalMode]
  );

  const getMessagesUrl = useCallback(
    (cid) => {
      if (isHospitalMode) return ENDPOINTS.GET_HOSPITAL_CONVERSATION_MESSAGES(cid);
      if (isProviderMode) return ENDPOINTS.GET_DOCTOR_CONVERSATION_MESSAGES(cid);
      return ENDPOINTS.GET_CONVERSATION_MESSAGES(cid);
    },
    [isHospitalMode, isProviderMode]
  );

  const bidAgreed = useMemo(() => {
    const b = bid;
    if (!b) return false;
    if (b.providerAction === "accepted_patient_price" && b.patientAction === "accepted") return true;
    if (b.providerAction === "countered" && b.patientAction === "accepted") return true;
    return false;
  }, [bid]);

  const hasAppointmentApproved = messages.some(
    (m) => m?.messageType === "system" && String(m?.payload?.type || "") === "appointment_approved"
  );

  const loadConversation = useCallback(async (opts = {}) => {
    const quiet = !!opts.quiet;
    if (!bidId || !open) return null;

    if (!quiet) {
      setLoading(true);
      setError("");
      setReverseBidError("");
      setReverseBidSubmitting(false);
    }

    try {
      const res = await API.get(getConversationUrl, { timeout: CONVERSATION_REQUEST_TIMEOUT });
      const data = res?.data ?? res;
      const cid = data.conversationId != null ? String(data.conversationId) : "";
      setConversationId(cid || null);
      setBid(data.bid ?? initialBidRef.current ?? null);
      setAppointment(data.appointment || null);
      setAgreedPrice(data.agreedPrice != null ? Number(data.agreedPrice) : null);
      setTreatmentBill(data.treatmentBill ?? null);
      setCaseClose(data.caseClose ?? null);
      if (data.treatmentBill && isProviderMode) {
        setBillFinalAmount(String(data.treatmentBill.finalAmount ?? ""));
        setBillPaymentMethod(data.treatmentBill.paymentMethod || "cash");
        setBillChargedMore(!!data.treatmentBill.chargedMoreThanAgreed);
        setBillReason(data.treatmentBill.reasonIfMore || "");
        setBillNotes(data.treatmentBill.providerNotes || "");
      }
      if (data.patientOnline !== undefined && data.providerOnline !== undefined) {
        if (isPatientMode) {
          setPeerOnline(!!data.providerOnline);
          setPeerLastSeenAt(data.providerLastSeenAt ? String(data.providerLastSeenAt) : null);
        } else {
          setPeerOnline(!!data.patientOnline);
          setPeerLastSeenAt(data.patientLastSeenAt ? String(data.patientLastSeenAt) : null);
        }
      }
      if (!quiet) setLoading(false);
      return { conversationId: cid };
    } catch (e) {
      if (!quiet) {
        setError(apiErrorMessage(e, "Failed to load conversation"));
        setBid(initialBidRef.current || null);
        setCaseClose(null);
        setLoading(false);
      }
      return null;
    }
  }, [bidId, open, getConversationUrl, isPatientMode, isProviderMode]);

  const loadMessages = useCallback(
    async (cid, opts = {}) => {
      if (!cid) return;
      const quiet = !!opts.quiet;
      if (!quiet) setMessagesLoading(true);
      try {
        const res = await API.get(getMessagesUrl(cid), {
          params: { limit: 200, page: 1 },
          timeout: 15000,
        });
        const data = res?.data ?? res;
        const items = (data.items || data.messages || [])
          .slice()
          .sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
        setMessages(items);
      } catch (_) {
        // silent fallback
      } finally {
        if (!quiet) setMessagesLoading(false);
      }
    },
    [getMessagesUrl]
  );

  const handleVerifyCloseCase = useCallback(async () => {
    if (!bidId || !isProviderMode) return;
    const url = isHospitalMode ? ENDPOINTS.HOSPITAL_VERIFY_CLOSE_CASE(bidId) : ENDPOINTS.DOCTOR_VERIFY_CLOSE_CASE(bidId);
    setVerifyCloseSubmitting(true);
    try {
      await API.post(url, {});
      await loadConversation({ quiet: true });
      showAlert("Case closed. It will disappear from Patient Bids and Confirmed Patients.", { variant: "success" });
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Could not close case";
      const reasons = e?.response?.data?.reasons;
      showAlert(Array.isArray(reasons) && reasons.length ? `${msg} ${reasons.join(" ")}` : msg, { variant: "error" });
    } finally {
      setVerifyCloseSubmitting(false);
    }
  }, [bidId, isProviderMode, isHospitalMode, loadConversation]);

  const handleSubmitTreatmentBill = useCallback(async () => {
    if (!bidId || !isProviderMode) return;
    const raw = String(billFinalAmount || "").replace(/[^\d.]/g, "");
    const amt = Number(raw);
    if (!Number.isFinite(amt) || amt < 0) {
      setBillUiError("Enter a valid amount");
      setTreatmentBillSectionOpen(true);
      return;
    }
    const exceedsAgreed = agreedPrice != null && amt > agreedPrice + 1e-9;
    const needReason = exceedsAgreed || billChargedMore;
    if (needReason && (!billReason || billReason.trim().length < 3)) {
      setBillUiError("Explain why the patient was charged more (min. 3 characters).");
      setTreatmentBillSectionOpen(true);
      return;
    }
    setBillUiError("");
    setBillSubmitting(true);
    try {
      const url = isHospitalMode ? ENDPOINTS.HOSPITAL_TREATMENT_BILL(bidId) : ENDPOINTS.DOCTOR_TREATMENT_BILL(bidId);
      const res = await API.put(url, {
        finalAmount: amt,
        paymentMethod: billPaymentMethod,
        chargedMoreThanAgreed: billChargedMore,
        reasonIfMore: billReason,
        providerNotes: billNotes,
        lineItems: [],
      });
      const data = res?.data ?? res;
      setTreatmentBill(data.treatmentBill ?? null);
      const conv = await loadConversation({ quiet: true });
      const cid = conv?.conversationId || conversationId;
      if (cid) await loadMessages(cid);
    } catch (e) {
      setBillUiError(e?.response?.data?.message || e?.message || "Failed to save bill");
      setTreatmentBillSectionOpen(true);
    } finally {
      setBillSubmitting(false);
    }
  }, [
    bidId,
    isProviderMode,
    isHospitalMode,
    billFinalAmount,
    agreedPrice,
    billChargedMore,
    billReason,
    billPaymentMethod,
    billNotes,
    loadConversation,
    loadMessages,
    conversationId,
  ]);

  const handlePatientBillRespond = useCallback(
    async (action) => {
      if (!bidId || !isPatientMode) return;
      const note = action === "dispute" ? patientBillDisputeNote.trim() : "";
      if (action === "dispute" && note.length < 3) {
        setBillUiError("Add a short note for the dispute (min. 3 characters).");
        return;
      }
      setBillUiError("");
      setPatientBillSubmitting(true);
      try {
        await API.post(ENDPOINTS.PATIENT_TREATMENT_BILL_RESPOND(bidId), {
          action,
          note: action === "dispute" ? note : undefined,
        });
        setPatientBillDisputeNote("");
        const conv = await loadConversation({ quiet: true });
        const cid = conv?.conversationId || conversationId;
        if (cid) await loadMessages(cid);
      } catch (e) {
        setBillUiError(e?.response?.data?.message || e?.message || "Failed to send");
      } finally {
        setPatientBillSubmitting(false);
      }
    },
    [bidId, isPatientMode, patientBillDisputeNote, loadConversation, loadMessages, conversationId]
  );

  const loadBookingSlots = useCallback(async () => {
    if (!bidId) return;
    if (!isPatientMode && !isProviderMode) return;
    setBookingSlotsLoading(true);
    try {
      const res = await API.get(ENDPOINTS.GET_BID_BOOKING_SLOTS(bidId), { timeout: 15000 });
      const data = res?.data ?? res;
      setBookingSlots(Array.isArray(data?.slots) ? data.slots : []);
    } catch (e) {
      setError(apiErrorMessage(e, "Failed to load available slots"));
      setBookingSlots([]);
    } finally {
      setBookingSlotsLoading(false);
    }
  }, [bidId, isPatientMode, isProviderMode]);

  useEffect(() => {
    if (!open || !bidId) return;
    loadConversation();
  }, [open, bidId, loadConversation]);

  useEffect(() => {
    if (!conversationId) return;
    loadMessages(conversationId);
  }, [conversationId, loadMessages]);

  useEffect(() => {
    if (hasAppointmentApproved) setShowBooking(false);
  }, [hasAppointmentApproved]);

  useEffect(() => {
    if (open) setTreatmentBillSectionOpen(false);
  }, [open, bidId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, peerTyping]);

  /** Mark peer messages as read (WhatsApp-style receipts). */
  useEffect(() => {
    if (!open || !conversationId || !messages.length) return;
    const socketRole = isProviderMode ? mode : "patient";
    const socket = getSocket({ role: socketRole });
    if (!socket) return;

    const ids = messages
      .filter((m) => {
        if (m.messageType === "system") return false;
        if (isPatientMode) return ["doctor", "hospital", "admin"].includes(m.senderRole);
        return m.senderRole === "user" || m.senderRole === "admin";
      })
      .map((m) => m._id)
      .filter(Boolean);

    if (!ids.length) return;
    const t = window.setTimeout(() => {
      try {
        socket.emit("message:read", { conversationId: String(conversationId), messageIds: ids });
      } catch (_) {}
    }, 250);
    return () => window.clearTimeout(t);
  }, [open, conversationId, messages, isPatientMode, isProviderMode, mode]);

  /** Join Socket.IO conversation room so server can push message:new to this client. */
  useEffect(() => {
    if (!open || !conversationId) return;
    const socketRole = isProviderMode ? mode : "patient";
    const socket = getSocket({ role: socketRole });
    if (!socket) return;
    const cid = String(conversationId);
    setPeerOnline(false);
    setActiveChatConversationId(cid);
    const join = () => {
      try {
        socket.emit("conversation:join", { conversationId: cid });
      } catch (_) {}
    };
    socket.on("connect", join);
    join();
    return () => {
      socket.off("connect", join);
      try {
        socket.emit("conversation:leave", { conversationId: cid });
      } catch (_) {}
      clearActiveChatConversationId(cid);
      setPeerOnline(false);
      setPeerTyping(false);
    };
  }, [open, conversationId, isProviderMode, mode]);

  useEffect(() => {
    if (!open || !bidId) return;
    const socketRole = isProviderMode ? mode : "patient";
    const socket = getSocket({ role: socketRole });
    const currentBidId = String(bidId).trim();

    let pollId = null;
    const refreshFromRest = () => {
      try {
        loadConversation({ quiet: true });
        if (conversationId) loadMessages(conversationId, { quiet: true });
      } catch (_) {}
    };
    const startPolling = () => {
      if (pollId) return;
      pollId = window.setInterval(refreshFromRest, 15000);
    };
    const stopPolling = () => {
      if (!pollId) return;
      window.clearInterval(pollId);
      pollId = null;
    };

    if (!socket) {
      startPolling();
      return () => stopPolling();
    }

    const onBidUpdate = (payload) => {
      if (!payload) return;
      const id = String(payload.bidId ?? payload.bidLogId ?? payload._id ?? "").trim();
      if (id && id === currentBidId) loadConversation();
    };

    const onRealtimeNotify = (payload) => {
      if (!payload) return;
      const type = String(payload.type || "").toLowerCase();
      // Chat thread already handles message:new — skip duplicate chat_message reloads.
      if (type === "chat_message" || type === "chat_reply") return;
      const bidPayloadId = String(payload.bidId ?? payload.bidLogId ?? payload._id ?? "").trim();
      if (bidPayloadId && bidPayloadId === currentBidId) {
        loadConversation();
        return;
      }
      const convId = String(payload.conversationId || "").trim();
      if (convId && conversationId && convId === String(conversationId)) {
        loadMessages(conversationId);
        loadConversation();
      }
    };

    const onMessageNew = (payload) => {
      const convId = String(payload?.conversationId || "").trim();
      if (!convId || !conversationId || convId !== String(conversationId)) return;
      const raw = payload?.data != null ? payload.data : payload;
      if (raw && raw._id && !shouldShowAdminMessageForViewer(raw, isPatientMode, mode)) {
        return;
      }
      if (raw && raw._id) {
        setMessages((prev) => {
          if (prev.some((m) => String(m._id) === String(raw._id))) return prev;
          return [...prev, raw].sort(
            (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0)
          );
        });
        return;
      }
      loadMessages(conversationId);
    };

    const onMessageRead = (payload) => {
      const convId = String(payload?.conversationId || "").trim();
      if (!convId || String(conversationId) !== convId) return;
      const ids = new Set((payload.messageIds || []).map((x) => String(x)));
      const by = payload.by;
      setMessages((prev) =>
        prev.map((m) => {
          if (!ids.has(String(m._id))) return m;
          const rb = Array.isArray(m.readBy) ? [...m.readBy] : [];
          if (by && !rb.some((r) => String(r.id) === String(by.id))) {
            rb.push({ id: String(by.id), role: by.role, at: new Date().toISOString() });
          }
          return { ...m, readBy: rb };
        })
      );
    };

    const onMessageDelivered = (payload) => {
      const convId = String(payload?.conversationId || "").trim();
      if (!convId || String(conversationId) !== convId) return;
      const mid = String(payload.messageId || "");
      const to = payload.to;
      if (!mid) return;
      setMessages((prev) =>
        prev.map((m) => {
          if (String(m._id) !== mid) return m;
          const dt = Array.isArray(m.deliveredTo) ? [...m.deliveredTo] : [];
          if (to && !dt.some((d) => String(d.id) === String(to.id))) {
            dt.push({ id: String(to.id), role: to.role, at: new Date().toISOString() });
          }
          return { ...m, deliveredTo: dt };
        })
      );
    };

    const onPresence = (payload) => {
      const convId = String(payload?.conversationId || "").trim();
      if (!convId || String(conversationId) !== convId) return;
      const ur = String(payload?.user?.role || "").toLowerCase();
      const isPeer = isPatientMode ? ur === "doctor" || ur === "hospital" : ur === "user";
      if (!isPeer) return;
      if (payload.state === "online") {
        setPeerOnline(true);
        setPeerLastSeenAt(null);
      }
      if (payload.state === "offline") {
        setPeerOnline(false);
        if (payload.lastSeenAt) setPeerLastSeenAt(String(payload.lastSeenAt));
      }
    };

    const onPresenceSync = (payload) => {
      const convId = String(payload?.conversationId || "").trim();
      if (!convId || String(conversationId) !== convId) return;
      if (isPatientMode) {
        if (typeof payload.providerOnline !== "boolean") return;
        setPeerOnline(!!payload.providerOnline);
        setPeerLastSeenAt(
          payload.providerOnline ? null : payload.providerLastSeenAt ? String(payload.providerLastSeenAt) : null
        );
      } else {
        if (typeof payload.patientOnline !== "boolean") return;
        setPeerOnline(!!payload.patientOnline);
        setPeerLastSeenAt(
          payload.patientOnline ? null : payload.patientLastSeenAt ? String(payload.patientLastSeenAt) : null
        );
      }
    };

    const onTyping = (payload) => {
      const convId = String(payload?.conversationId || "").trim();
      if (!convId || String(conversationId) !== convId) return;
      const myRole = isPatientMode ? "user" : mode;
      if (String(payload?.from?.role) === String(myRole)) return;
      setPeerTyping(!!payload.isTyping);
      if (payload.isTyping) {
        window.clearTimeout(typingStopTimerRef.current);
        typingStopTimerRef.current = window.setTimeout(() => setPeerTyping(false), 4000);
      }
    };

    const onBookingSlotsUpdated = (payload) => {
      const convId = String(payload?.conversationId || "").trim();
      if (!convId || !conversationId || convId !== String(conversationId)) return;
      if ((isPatientMode && showBooking) || isProviderMode) loadBookingSlots();
    };

    socket.on("bid:update", onBidUpdate);
    socket.on("notification:new", onRealtimeNotify);
    socket.on("message:new", onMessageNew);
    socket.on("message:read", onMessageRead);
    socket.on("message:delivered", onMessageDelivered);
    socket.on("presence:peer", onPresence);
    socket.on("conversation:presenceSync", onPresenceSync);
    socket.on("typing", onTyping);
    socket.on("booking_slots:updated", onBookingSlotsUpdated);
    socket.on("connect", stopPolling);
    socket.on("disconnect", startPolling);
    socket.on("connect_error", startPolling);
    if (!socket.connected) startPolling();

    return () => {
      socket.off("bid:update", onBidUpdate);
      socket.off("notification:new", onRealtimeNotify);
      socket.off("message:new", onMessageNew);
      socket.off("message:read", onMessageRead);
      socket.off("message:delivered", onMessageDelivered);
      socket.off("presence:peer", onPresence);
      socket.off("conversation:presenceSync", onPresenceSync);
      socket.off("typing", onTyping);
      socket.off("booking_slots:updated", onBookingSlotsUpdated);
      socket.off("connect", stopPolling);
      socket.off("disconnect", startPolling);
      socket.off("connect_error", startPolling);
      stopPolling();
    };
  }, [
    open,
    bidId,
    conversationId,
    mode,
    isProviderMode,
    isPatientMode,
    loadConversation,
    loadMessages,
    showBooking,
    loadBookingSlots,
  ]);

  const refreshBid = useCallback(() => {
    if (requestId && dispatch && getMatchResults) {
      dispatch(getMatchResults(requestId));
      if (getProviderCount) dispatch(getProviderCount(requestId));
    }
    onBidUpdated?.();
  }, [requestId, dispatch, getMatchResults, getProviderCount, onBidUpdated]);

  useEffect(() => {
    if (!showBooking || !isPatientMode || !bidId) return;
    loadBookingSlots();
  }, [showBooking, isPatientMode, bidId, loadBookingSlots]);

  useEffect(() => {
    if (!open || !bidId || !isProviderMode || !appointment?._id || !bidAgreed) return;
    loadBookingSlots();
  }, [open, bidId, isProviderMode, appointment?._id, bidAgreed, loadBookingSlots]);

  const handleAccept = async () => {
    if (!bidId || !isPatientMode) return;
    try {
      await API.post(ENDPOINTS.RESPOND_TO_BID(bidId), { action: "accept" });
      setBid((b) => (b ? { ...b, patientAction: "accepted", providerAction: "countered" } : b));
      const conv = await loadConversation({ quiet: true });
      const cid = conv?.conversationId || conversationId;
      if (cid) await loadMessages(cid);
      refreshBid();
    } catch (e) {
      setError(apiErrorMessage(e, "Failed to accept"));
    }
  };

  const handleReject = async () => {
    if (!bidId) return;
    try {
      if (isPatientMode) {
        await API.post(ENDPOINTS.RESPOND_TO_BID(bidId), { action: "reject" });
        setBid((b) => (b ? { ...b, patientAction: "rejected", providerAction: "rejected" } : b));
      } else {
        await API.post(getProviderRejectUrl(bidId), {});
        setBid((b) => (b ? { ...b, providerAction: "rejected", patientAction: "rejected" } : b));
      }
      const conv = await loadConversation({ quiet: true });
      const cid = conv?.conversationId || conversationId;
      if (cid) await loadMessages(cid);
      refreshBid();
    } catch (e) {
      setError(apiErrorMessage(e, "Failed to reject"));
    }
  };

  const handleReverseBid = async () => {
    const amount = bidAmountInputToNumber(reverseBidAmount);
    const digits = bidAmountDigitsOnly(reverseBidAmount);
    const phoneLike = validateBidAmountDigitsNotPhoneLike(digits);
    if (phoneLike) {
      setReverseBidError(phoneLike);
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      setReverseBidError("Enter a valid amount.");
      return;
    }

    setReverseBidSubmitting(true);
    setReverseBidError("");
    setError("");

    try {
      if (isPatientMode) {
        await API.post(
          ENDPOINTS.PATIENT_COUNTER_BID(bidId),
          { amount },
          { timeout: 20000 }
        );
      } else {
        await API.post(
          getProviderCounterUrl(bidId),
          { reverseBidAmount: amount },
          { timeout: 20000 }
        );
      }

      setReverseBidAmount("");
      setReverseBidError("");
      setShowReverseBidInput(false);

      setBid((b) => {
        if (!b) return b;

        if (isPatientMode) {
          return {
            ...b,
            patientAction: "countered",
            patientCounterAmount: amount,
          };
        }

        return {
          ...b,
          providerAction: "countered",
          reverseBidAmount: amount,
          patientAction: "pending",
        };
      });

      const conv = await loadConversation({ quiet: true });
      const cid = conv?.conversationId || conversationId;
      if (cid) await loadMessages(cid);
      refreshBid();
    } catch (e) {
      setReverseBidError(apiErrorMessage(e, "Failed to submit reverse bid"));
    } finally {
      setReverseBidSubmitting(false);
    }
  };

  const handleAcceptProvider = async () => {
    if (!bidId || !isProviderMode) return;
    setError("");
    try {
      await API.post(getProviderAcceptUrl(bidId), {});
      setBid((b) =>
        b ? { ...b, providerAction: "accepted_patient_price", patientAction: "accepted" } : b
      );
      const conv = await loadConversation({ quiet: true });
      const cid = conv?.conversationId || conversationId;
      if (cid) await loadMessages(cid);
      refreshBid();
    } catch (e) {
      setError(apiErrorMessage(e, "Failed to accept"));
    }
  };

  const handleAppointmentAccept = async () => {
    if (!appointment?._id || !isProviderMode) return;

    setAppointmentSaving("accept");
    setError("");

    try {
      await API.patch(getProviderAppointmentRespondUrl(appointment._id), {
        action: "accept",
      });

      setAppointment((a) =>
        a
          ? {
              ...a,
              doctorAction: "accepted",
            }
          : a
      );

      const conv = await loadConversation({ quiet: true });
      const cid = conv?.conversationId || conversationId;
      if (cid) await loadMessages(cid);
      onBidUpdated?.();
    } catch (e) {
      setError(apiErrorMessage(e, "Failed to accept appointment"));
    } finally {
      setAppointmentSaving(null);
    }
  };

  const handleAppointmentReschedule = async () => {
    if (
      !appointment?._id ||
      !appointmentRescheduleDate ||
      !appointmentRescheduleSlot ||
      !isProviderMode
    ) {
      setError("Please select date and time.");
      return;
    }

    setAppointmentSaving("reschedule");
    setError("");

    try {
      await API.patch(getProviderAppointmentRespondUrl(appointment._id), {
        action: "reschedule",
        proposedDate: appointmentRescheduleDate,
        proposedTimeSlot: appointmentRescheduleSlot,
      });

      setAppointment((a) =>
        a
          ? {
              ...a,
              doctorAction: "reschedule",
              doctorProposedDate: appointmentRescheduleDate,
              doctorProposedTimeSlot: appointmentRescheduleSlot,
            }
          : a
      );

      setAppointmentRescheduleDate("");
      setAppointmentRescheduleSlot("");
      const conv = await loadConversation({ quiet: true });
      const cid = conv?.conversationId || conversationId;
      if (cid) await loadMessages(cid);
      onBidUpdated?.();
    } catch (e) {
      setError(apiErrorMessage(e, "Failed to reschedule"));
    } finally {
      setAppointmentSaving(null);
    }
  };

  const handleAppointmentComplete = async () => {
    if (!appointment?._id || !isProviderMode) return;
    setAppointmentSaving("complete");
    setError("");
    try {
      await API.patch(getProviderAppointmentCompleteUrl(appointment._id), {});
      setAppointment((a) => (a ? { ...a, status: "completed" } : a));
      const conv = await loadConversation({ quiet: true });
      const cid = conv?.conversationId || conversationId;
      if (cid) await loadMessages(cid);
      onBidUpdated?.();
    } catch (e) {
      setError(apiErrorMessage(e, "Failed to complete appointment"));
    } finally {
      setAppointmentSaving(null);
    }
  };

  const handlePickBookingSlot = (slot) => {
    if (!slot?.appointmentDate || !slot?.timeSlot) return;
    setError("");
    setBookingSelection({
      appointmentDate: slot.appointmentDate,
      timeSlot: slot.timeSlot,
      clinicName: slot.clinicName || parseTimeSlotDisplay(slot.timeSlot).clinicLabel || "",
    });
  };

  const handleCloseBookingPanel = () => {
    setBookingSelection(null);
    setShowBooking(false);
  };

  const handleConfirmBooking = async () => {
    if (!bookingSelection?.appointmentDate || !bookingSelection?.timeSlot) {
      setError("Select a date and time, then tap Book visit.");
      return;
    }

    setBookingSubmitting(true);
    setError("");

    const apptSt = String(appointment?.status || "").toLowerCase();
    const apptDocAct = String(appointment?.doctorAction || "").toLowerCase();
    const updatingPendingSlot =
      isPatientMode && appointment?._id && apptSt === "pending_approval" && apptDocAct === "pending";

    try {
      if (updatingPendingSlot) {
        const res = await API.patch(ENDPOINTS.UPDATE_PENDING_APPOINTMENT_SLOT(appointment._id), {
          appointmentDate: bookingSelection.appointmentDate,
          timeSlot: bookingSelection.timeSlot,
        });
        const data = res?.data ?? res;
        const updated = data?.appointment;

        setShowBooking(false);
        setBookingSelection(null);

        if (updated && updated._id) {
          setAppointment((prev) => ({
            ...(prev || {}),
            ...updated,
            appointmentDate: updated.appointmentDate || bookingSelection.appointmentDate,
            timeSlot: updated.timeSlot || bookingSelection.timeSlot,
          }));
        }

        const conv = await loadConversation({ quiet: true });
        const cid = conv?.conversationId || conversationId;
        if (cid) await loadMessages(cid);
        refreshBid();
        loadBookingSlots();
      } else {
        const res = await API.post(ENDPOINTS.BOOK_APPOINTMENT, {
          bidId,
          appointmentDate: bookingSelection.appointmentDate,
          timeSlot: bookingSelection.timeSlot,
        });
        const data = res?.data ?? res;
        const created = data?.appointment;

        setShowBooking(false);
        setBookingSelection(null);

        if (created && created._id) {
          setAppointment({
            ...created,
            appointmentDate: created.appointmentDate || bookingSelection.appointmentDate,
          });
        } else {
          setAppointment((prev) =>
            prev || {
              status: "pending_approval",
              doctorAction: "pending",
              appointmentDate: bookingSelection.appointmentDate,
              timeSlot: bookingSelection.timeSlot,
              clinicName: parseTimeSlotDisplay(bookingSelection.timeSlot).clinicLabel || "",
            }
          );
        }

        const conv = await loadConversation({ quiet: true });
        const cid = conv?.conversationId || conversationId;
        if (cid) await loadMessages(cid);
        refreshBid();
        loadBookingSlots();
      }
    } catch (e) {
      const raw =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        (updatingPendingSlot ? "Could not update visit time" : "Failed to book");
      setError(raw);
    } finally {
      setBookingSubmitting(false);
    }
  };

  const groupedBookingSlots = useMemo(() => {
    const byDate = new Map();
    for (const s of bookingSlots) {
      const k = s.appointmentDate;
      if (!byDate.has(k)) byDate.set(k, []);
      byDate.get(k).push(s);
    }
    return [...byDate.entries()];
  }, [bookingSlots]);

  const apptSlotSource = String(
    appointment?.doctorProposedTimeSlot || appointment?.timeSlot || ""
  ).trim();

  const providerRescheduleSlotOptions = useMemo(() => {
    const tp = apptSlotSource ? parseTimeSlotDisplay(apptSlotSource).timePart || apptSlotSource : "";
    return mergeTimeSlotOptions(tp);
  }, [apptSlotSource]);

  /* eslint-disable react-hooks/exhaustive-deps -- primitives only: full `appointment` updates each poll and would wipe in-progress reschedule edits */
  useEffect(() => {
    if (!open || !bidId || !isProviderMode) return;
    if (!appointment?._id) return;
    const st = String(appointment?.status || "").toLowerCase();
    const docAct = String(appointment?.doctorAction || "").toLowerCase();
    const prefillPending = st === "pending_approval" && docAct === "pending";
    const prefillScheduled = st === "scheduled";
    if (!prefillPending && !prefillScheduled) return;

    setAppointmentRescheduleDate(appointmentDateForInput(appointment.appointmentDate));
    const tp = appointmentTimePart(appointment);
    const opts = mergeTimeSlotOptions(tp);
    setAppointmentRescheduleSlot(tp && opts.includes(tp) ? tp : "");
  }, [
    open,
    bidId,
    isProviderMode,
    appointment?._id,
    appointment?.appointmentDate,
    appointment?.timeSlot,
    appointment?.doctorProposedTimeSlot,
    appointment?.status,
    appointment?.doctorAction,
  ]);
  /* eslint-enable react-hooks/exhaustive-deps */

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      const mobileFullSheet =
        typeof window !== "undefined" && window.matchMedia(BHC_COMPACT_MEDIA).matches && !collapsed;
      if (mobileFullSheet) {
        setBookingSelection(null);
        setShowBooking(false);
        setCollapsed(false);
        onClose();
        return;
      }
      if (collapsed) {
        setBookingSelection(null);
        setShowBooking(false);
        onClose();
      } else {
        setCollapsed(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, collapsed, onClose]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia(BHC_COMPACT_MEDIA);
    const sync = () => {
      const narrow = mq.matches;
      setNarrowViewport(narrow);
      if (open && !collapsed && narrow) {
        document.documentElement.classList.add("healbid-bhc-mobile-open");
      } else {
        document.documentElement.classList.remove("healbid-bhc-mobile-open");
      }
    };
    sync();
    mq.addEventListener("change", sync);
    window.addEventListener("resize", sync);
    return () => {
      mq.removeEventListener("change", sync);
      window.removeEventListener("resize", sync);
      document.documentElement.classList.remove("healbid-bhc-mobile-open");
    };
  }, [open, collapsed]);

  if (!open) return null;

  const handleClose = () => {
    setBookingSelection(null);
    setShowBooking(false);
    setCollapsed(false);
    onClose();
  };

  const handleMinimize = (e) => {
    e.stopPropagation();
    setCollapsed(true);
  };

  const handleHeaderExpand = () => setCollapsed(false);

  const effectiveBid = bid || initialBid;
  const providerAction = effectiveBid?.providerAction || "";
  const patientAction = effectiveBid?.patientAction || "";

  const isProviderAccepted = providerAction === "accepted_patient_price";
  const isProviderCountered = providerAction === "countered";

  const canBook = isProviderAccepted || (isProviderCountered && patientAction === "accepted");
  const providerPending = providerAction === "pending";
  const patientPending = isProviderCountered && patientAction === "pending";

  const appointmentStatus = String(appointment?.status || "").toLowerCase();
  const appointmentDoctorAction = String(appointment?.doctorAction || "").toLowerCase();

  const appointmentPendingApproval = appointmentStatus === "pending_approval";
  const appointmentScheduled = appointmentStatus === "scheduled";
  const appointmentCompleted = appointmentStatus === "completed";

  const patientCanCreateAppointment =
    isPatientMode &&
    canBook &&
    !appointment &&
    !hasAppointmentApproved &&
    !appointmentScheduled &&
    !appointmentCompleted;

  const patientHasPendingAppointmentRequest =
    isPatientMode &&
    appointmentPendingApproval &&
    !appointmentScheduled &&
    !appointmentCompleted;

  /** Patient may pick another published slot only before the provider has acted on the request. */
  const patientCanChangePendingSlot =
    patientHasPendingAppointmentRequest &&
    appointmentDoctorAction === "pending" &&
    Boolean(appointment?._id);

  const patientBookingPickerOpen =
    (patientCanCreateAppointment && showBooking) || (patientCanChangePendingSlot && showBooking);

  const patientShouldSeeConfirmedAppointment =
    isPatientMode && (appointmentScheduled || hasAppointmentApproved);

  const bidSummaryAmount =
    patientAction === "countered" && typeof effectiveBid?.patientCounterAmount === "number"
      ? effectiveBid.patientCounterAmount
      : effectiveBid?.affordabilityPrice;

  const counterPartyLabel = mode === "hospital" ? "Hospital" : "Doctor";

  const subtitle =
    patientName && providerName
      ? `${patientName} with ${providerName}`
      : [patientName, providerName].filter(Boolean).join(" · ") || null;

  const peerAvatar = String(peerAvatarUrl || "").trim();

  const headerPresence =
    peerOnline ? (
      <span className="bhc-presence" title="Connected to the app">
        Online
      </span>
    ) : peerLastSeenAt ? (
      <span className="bhc-lastseen" title={peerLastSeenAt}>
        {formatLastSeen(peerLastSeenAt)}
      </span>
    ) : null;

  const fullScreenMobile = open && !collapsed && narrowViewport;

  const modalTree = (
    <div
      className={`bhc-widget-root${fullScreenMobile ? " bhc-widget-root--full-screen" : ""}`}
      role="dialog"
      aria-modal={fullScreenMobile ? "true" : "false"}
      aria-label="Bid history"
    >
      <div
        className={`bhc-widget ${collapsed ? "bhc-widget--collapsed" : "bhc-widget--expanded"}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={`bhc-header${collapsed ? " bhc-header--collapsed" : ""}`}
          aria-expanded={!collapsed}
        >
          {collapsed ? (
            <button
              type="button"
              className="bhc-header-main bhc-header-main--tap"
              onClick={handleHeaderExpand}
              aria-label="Expand conversation"
            >
              <div className="bhc-header-row">
                <span className="bhc-avatar-wrap">
                  {peerAvatar ? (
                    <img src={peerAvatar} alt="" className="bhc-avatar-img" />
                  ) : (
                    <span className="bhc-avatar-placeholder" aria-hidden />
                  )}
                  {peerOnline ? (
                    <span className="bhc-presence-dot" title="Online" aria-label="Online" />
                  ) : null}
                </span>
                <div className="bhc-header-text-stack">
                  <h3 className="bhc-title">Bid history</h3>
                  {subtitle ? <p className="bhc-subtitle">{subtitle}</p> : null}
                  {headerPresence}
                </div>
              </div>
            </button>
          ) : (
            <div className="bhc-header-main">
              <div className="bhc-header-row">
                <span className="bhc-avatar-wrap">
                  {peerAvatar ? (
                    <img src={peerAvatar} alt="" className="bhc-avatar-img" />
                  ) : (
                    <span className="bhc-avatar-placeholder" aria-hidden />
                  )}
                  {peerOnline ? (
                    <span className="bhc-presence-dot" title="Online" aria-label="Online" />
                  ) : null}
                </span>
                <div className="bhc-header-text-stack">
                  <h3 className="bhc-title">Bid history</h3>
                  {subtitle ? <p className="bhc-subtitle">{subtitle}</p> : null}
                  {headerPresence}
                </div>
              </div>
            </div>
          )}
          <div className="bhc-widget-header-actions">
            {!collapsed && (
              <button
                type="button"
                className="bhc-icon-btn"
                onClick={handleMinimize}
                aria-label="Minimize"
                title="Minimize"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
            <button
              type="button"
              className="bhc-close"
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
              }}
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>

        <div className="bhc-widget-body">
          <div className="bhc-widget-body-main">
        {loading && (
          <div className="bhc-loading" aria-live="polite">
            <div style={{ padding: 16 }}>
              <SkeletonBlock height={16} width="35%" />
              <div style={{ height: 12 }} />
              <SkeletonBlock height={76} width="100%" />
              <div style={{ height: 12 }} />
              <SkeletonText lines={6} lineHeight={14} gap={10} />
            </div>
          </div>
        )}

        {error && <div className="bhc-error">{error}</div>}

        {!loading && (bid || initialBid) && (
          <>
            <div className="bhc-pricing-snapshot" aria-label="Pricing summary">
              <p className="bhc-pricing-snapshot__eyebrow">Offer summary</p>
              <div className="bhc-pricing-snapshot__cards">
                <article className="bhc-price-card bhc-price-card--patient">
                  <span className="bhc-price-card__label">
                    {patientAction === "countered" && typeof effectiveBid?.patientCounterAmount === "number"
                      ? isPatientMode
                        ? "Your latest offer"
                        : "Patient counter-offer"
                      : isPatientMode
                        ? "Your offer"
                        : "Patient offer"}
                  </span>
                  <span className="bhc-price-card__amount">
                    {bidSummaryAmount != null && Number.isFinite(Number(bidSummaryAmount))
                      ? fmtINR(Number(bidSummaryAmount))
                      : "—"}
                  </span>
                  {patientAction === "countered" &&
                  typeof effectiveBid?.patientCounterAmount === "number" &&
                  typeof effectiveBid?.affordabilityPrice === "number" &&
                  Number(effectiveBid.affordabilityPrice) !== Number(effectiveBid.patientCounterAmount) ? (
                    <span className="bhc-price-card__sub">
                      First ask {fmtINR(effectiveBid.affordabilityPrice)}
                    </span>
                  ) : null}
                </article>
                {effectiveBid?.reverseBidAmount != null ? (
                  <>
                    <span className="bhc-pricing-snapshot__arrow" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                        <path
                          d="M4 10h12M12 6l4 4-4 4"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    <article className="bhc-price-card bhc-price-card--provider">
                      <span className="bhc-price-card__label">
                        {isPatientMode ? `${counterPartyLabel} counter-offer` : "Your counter-offer"}
                      </span>
                      <span className="bhc-price-card__amount">{fmtINR(effectiveBid.reverseBidAmount)}</span>
                    </article>
                  </>
                ) : null}
              </div>
            </div>

            <div className="bhc-body-scroll">
              <div className="bhc-messages">
              {messagesLoading && messages.length === 0 && (
                <div style={{ padding: 14 }}>
                  <SkeletonText lines={7} lineHeight={14} gap={12} />
                </div>
              )}

              {messages.map((m) => {
                const side = getMessageSide(m);
                const wrapClass =
                  side === "patient"
                    ? "bhc-msg-wrap bhc-from-patient"
                    : side === "provider"
                      ? "bhc-msg-wrap bhc-from-provider"
                      : "bhc-msg-wrap bhc-from-system";

                if (m.messageType === "system") {
                  if (isLedgerPriceSystemMessage(m)) {
                    return (
                      <div key={m._id} className="bhc-msg-wrap bhc-msg-wrap--ledger">
                        <BidLedgerSystemMessage m={m} mode={mode} />
                      </div>
                    );
                  }
                  if (isAppointmentSystemCard(m)) {
                    return (
                      <div key={m._id} className="bhc-msg-wrap bhc-msg-wrap--appt-card">
                        <AppointmentSystemMessage m={m} mode={mode} />
                      </div>
                    );
                  }
                  if (isNegotiationOutcomeCard(m)) {
                    return (
                      <div key={m._id} className="bhc-msg-wrap bhc-msg-wrap--deal-card">
                        <NegotiationOutcomeMessage m={m} mode={mode} />
                      </div>
                    );
                  }
                  const text = getSystemMessageText(m, mode);
                  return (
                    <div key={m._id} className={wrapClass}>
                      <div
                        className={
                          side === "patient"
                            ? "bhc-msg-from-patient"
                            : side === "provider"
                              ? "bhc-msg-from-provider"
                              : "bhc-system-msg"
                        }
                      >
                        {text}
                        <div className="bhc-msg-time-row">
                          <span className="bhc-msg-time">{formatChatTime(m.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={m._id} className={wrapClass}>
                    <div
                      className={`${
                        side === "patient"
                          ? "bhc-msg-from-patient"
                          : side === "provider"
                            ? "bhc-msg-from-provider"
                            : "bhc-text-msg"
                      } ${m.senderRole === "admin" ? "bhc-admin" : ""}`}
                    >
                      {m.senderRole === "admin"
                        ? `Healbid Admin Says: "${m.text ?? ""}"`
                        : m.text}
                      <div className="bhc-msg-time-row">
                        <span className="bhc-msg-time">{formatChatTime(m.createdAt)}</span>
                        {isOutgoingMessage(m, mode) ? (
                          <span className="bhc-msg-ticks" aria-hidden>
                            <ChatDeliveryTicks
                              status={getOutgoingReceiptStatus(m)}
                              theme={side === "provider" ? "onDark" : "mint"}
                            />
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })}
              {peerTyping && (
                <div className="bhc-typing" aria-live="polite">
                  Typing…
                </div>
              )}
              <div ref={messagesEndRef} />
              </div>

              <div className="bhc-actions">
              {isPatientMode && (
                <>
                  {patientPending && !appointmentPendingApproval && (
                    <div className="bhc-appointment-card">
                      <p className="bhc-booking-title">Price update</p>
                      <p className="bhc-muted" style={{ marginTop: 4, marginBottom: 12 }}>
                        {isProviderCountered && effectiveBid?.reverseBidAmount != null ? (
                          <>
                            {counterPartyLabel} counter-offered{" "}
                            <strong>{fmtINR(Number(effectiveBid.reverseBidAmount))}</strong>. Accept, send your own
                            counter-offer (₹), or decline.
                          </>
                        ) : (
                          <>
                            {counterPartyLabel} accepted your opening bid. Accept to lock it in and book a visit, or
                            decline.
                          </>
                        )}
                      </p>
                      <div className="bhc-btn-row">
                        <button type="button" className="bhc-btn bhc-btn-primary" onClick={handleAccept}>
                          Accept
                        </button>

                        {isProviderCountered ? (
                          !showReverseBidInput ? (
                            <button
                              type="button"
                              className="bhc-btn bhc-btn-outline"
                              onClick={() => setShowReverseBidInput(true)}
                            >
                              Counter-offer
                            </button>
                          ) : (
                            <div className="bhc-reverse-inline">
                              <input
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                min="1"
                                value={reverseBidAmount}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  if (bidAmountRawHasDisallowedSymbols(raw)) {
                                    setReverseBidError(BID_AMOUNT_CONTACT_BLOCKED_MSG);
                                    return;
                                  }
                                  setReverseBidError("");
                                  setReverseBidAmount(formatIndianBidAmountInput(raw));
                                }}
                                onKeyDown={(e) => {
                                  if (["e", "E", "+", "-", "."].includes(e.key)) e.preventDefault();
                                }}
                                placeholder="Amount (₹)"
                              />
                              <button
                                type="button"
                                className="bhc-btn bhc-btn-primary"
                                onClick={handleReverseBid}
                                disabled={reverseBidSubmitting}
                              >
                                Send counter
                              </button>
                              <button
                                type="button"
                                className="bhc-btn bhc-btn-outline"
                                onClick={() => {
                                  setShowReverseBidInput(false);
                                  setReverseBidAmount("");
                                  setReverseBidError("");
                                }}
                              >
                                Cancel
                              </button>
                              {reverseBidError && <span className="bhc-inline-err">{reverseBidError}</span>}
                            </div>
                          )
                        ) : null}

                        <button type="button" className="bhc-btn bhc-btn-danger" onClick={handleReject}>
                          Decline
                        </button>
                      </div>
                    </div>
                  )}

                  {patientCanCreateAppointment && (
                    <>
                      <p className="bhc-muted" style={{ marginBottom: 10 }}>
                        Offer agreed — choose a visit slot below. You can still message your {counterPartyLabel.toLowerCase()}{" "}
                        above.
                      </p>
                      {!showBooking ? (
                        <div className="bhc-btn-row">
                          <button
                            type="button"
                            className="bhc-btn bhc-btn-primary"
                            onClick={() => setShowBooking(true)}
                          >
                            Book Appointment
                          </button>
                          <button
                            type="button"
                            className="bhc-btn bhc-btn-outline"
                            onClick={handleReject}
                          >
                            Reject
                          </button>
                        </div>
                      ) : null}
                    </>
                  )}

                  {patientCanChangePendingSlot && !showBooking ? (
                    <div className="bhc-appointment-card">
                      <p className="bhc-booking-title">Visit request pending</p>
                      <p className="bhc-muted" style={{ marginTop: 6 }}>
                        Your {counterPartyLabel.toLowerCase()} has not responded yet. You may pick another published time
                        from their calendar; we will notify them in this thread.
                      </p>
                      {appointment ? (
                        <div className="bhc-visit-detail" style={{ marginTop: 10 }}>
                          <p className="bhc-visit-detail__line">
                            <strong>Current request:</strong> {appointmentTimePart(appointment)}
                            {appointment.appointmentDate
                              ? ` · ${new Date(appointment.appointmentDate).toLocaleDateString()}`
                              : ""}
                          </p>
                          {appointmentClinicLabel(appointment) ? (
                            <p className="bhc-visit-detail__line">
                              <strong>Clinic / location:</strong>{" "}
                              <span className="bhc-booking-clinic">{appointmentClinicLabel(appointment)}</span>
                            </p>
                          ) : null}
                        </div>
                      ) : null}
                      <div className="bhc-btn-row" style={{ marginTop: 12 }}>
                        <button type="button" className="bhc-btn bhc-btn-primary" onClick={() => setShowBooking(true)}>
                          Change visit time
                        </button>
                      </div>
                    </div>
                  ) : null}

                  {patientBookingPickerOpen ? (
                    <div className="bhc-booking">
                      <p className="bhc-booking-title">
                        {patientCanChangePendingSlot
                          ? "Pick a new slot from published availability"
                          : "Select from this provider’s availability"}
                      </p>
                      <p className="bhc-muted" style={{ marginTop: 6, marginBottom: 10 }}>
                        {patientCanChangePendingSlot
                          ? "Tap a slot, then confirm to replace your pending request. Your provider still needs to review it."
                          : "Tap a slot to choose it, then Book visit to send your request. If anything clashes, our team will reach out. Availability updates in real time."}
                      </p>

                      {bookingSlotsLoading && (
                        <p className="bhc-muted" style={{ marginBottom: 8 }}>
                          Loading slots…
                        </p>
                      )}

                      {!bookingSlotsLoading && bookingSlots.length === 0 && (
                        <p className="bhc-muted" style={{ marginBottom: 8 }}>
                          No bookable slots in the next two weeks. Check back later or message the provider.
                        </p>
                      )}

                      {!bookingSlotsLoading &&
                        groupedBookingSlots.map(([dateStr, slots]) => (
                          <div key={dateStr} className="bhc-booking-date-group">
                            <div className="bhc-booking-row">
                              <label>{formatBookingDateLabel(dateStr)}</label>
                              <div className="bhc-slots">
                                {slots.map((s) => {
                                  const key = `${s.appointmentDate}|${s.timeSlot}`;
                                  const selected =
                                    bookingSelection?.appointmentDate === s.appointmentDate &&
                                    bookingSelection?.timeSlot === s.timeSlot;
                                  const clinic = slotClinicLabel(s);
                                  return (
                                    <button
                                      key={key}
                                      type="button"
                                      className={`bhc-slot ${selected ? "active" : ""}`}
                                      onClick={() => handlePickBookingSlot(s)}
                                    >
                                      <span className="bhc-slot__time">{slotTimePart(s)}</span>
                                      {clinic ? <span className="bhc-slot__clinic">{clinic}</span> : null}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        ))}

                      {bookingSelection?.appointmentDate && bookingSelection?.timeSlot ? (
                        <div className="bhc-booking-selection-summary" style={{ marginTop: 8 }}>
                          <p className="bhc-muted" style={{ margin: 0 }}>
                            <strong>Your visit time:</strong> {slotTimePart(bookingSelection)} on{" "}
                            {formatBookingDateLabel(bookingSelection.appointmentDate)}
                            {String(bookingSelection.clinicName || "").trim() ? (
                              <>
                                {" "}
                                · <span className="bhc-booking-clinic">{String(bookingSelection.clinicName).trim()}</span>
                              </>
                            ) : null}
                          </p>
                        </div>
                      ) : null}

                      <div className="bhc-btn-row">
                        <button
                          type="button"
                          className="bhc-btn bhc-btn-primary"
                          onClick={handleConfirmBooking}
                          disabled={
                            !bookingSelection?.appointmentDate ||
                            !bookingSelection?.timeSlot ||
                            bookingSubmitting
                          }
                        >
                          {patientCanChangePendingSlot
                            ? bookingSubmitting
                              ? "Updating…"
                              : "Update visit time"
                            : bookingSubmitting
                              ? "Booking…"
                              : "Book visit"}
                        </button>
                        <button type="button" className="bhc-btn bhc-btn-outline" onClick={handleCloseBookingPanel}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : null}

                  {patientHasPendingAppointmentRequest && !patientCanChangePendingSlot ? (
                    <div className="bhc-appointment-card">
                      <div className="bhc-btn-row">
                        <button
                          type="button"
                          className="bhc-btn bhc-btn-primary"
                          disabled
                          style={{ opacity: 0.6, cursor: "not-allowed" }}
                        >
                          Book Appointment Requested
                        </button>
                      </div>
                      <p className="bhc-muted" style={{ marginTop: 10 }}>
                        Your appointment request is already submitted and is waiting for doctor/hospital review and admin approval.
                      </p>
                      {appointment ? (
                        <div className="bhc-visit-detail" style={{ marginTop: 10 }}>
                          <p className="bhc-visit-detail__line">
                            <strong>When:</strong> {appointmentTimePart(appointment)}
                            {appointment.appointmentDate
                              ? ` · ${new Date(appointment.appointmentDate).toLocaleDateString()}`
                              : ""}
                          </p>
                          {appointmentClinicLabel(appointment) ? (
                            <p className="bhc-visit-detail__line">
                              <strong>Clinic / location:</strong>{" "}
                              <span className="bhc-booking-clinic">{appointmentClinicLabel(appointment)}</span>
                            </p>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {patientShouldSeeConfirmedAppointment && (
                    <div className="bhc-appointment-card">
                      <p className="bhc-booking-title">Appointment confirmed</p>
                      <div className="bhc-visit-detail">
                        <p className="bhc-visit-detail__line">
                          <strong>When:</strong> {appointment ? appointmentTimePart(appointment) : "Time pending"}
                          {appointment?.appointmentDate
                            ? ` · ${new Date(appointment.appointmentDate).toLocaleDateString()}`
                            : ""}
                        </p>
                        {appointment && appointmentClinicLabel(appointment) ? (
                          <p className="bhc-visit-detail__line">
                            <strong>Clinic / location:</strong>{" "}
                            <span className="bhc-booking-clinic">{appointmentClinicLabel(appointment)}</span>
                          </p>
                        ) : null}
                      </div>
                    </div>
                  )}

                  {bidAgreed && treatmentBill && (
                    <div className="bhc-appointment-card" style={{ marginTop: 12 }}>
                      <p className="bhc-booking-title">Treatment bill</p>
                      <p className="bhc-muted">
                        Agreed offer: {agreedPrice != null ? fmtINR(agreedPrice) : "—"} · Charged:{" "}
                        {fmtINR(treatmentBill.finalAmount)} · {treatmentBill.paymentMethod}
                      </p>
                      {treatmentBill.lineItems?.length > 0 && (
                        <ul style={{ margin: "8px 0", paddingLeft: 18, fontSize: 13 }}>
                          {treatmentBill.lineItems.map((row, i) => (
                            <li key={i}>
                              {row.label}: {fmtINR(row.amount)}
                            </li>
                          ))}
                        </ul>
                      )}
                      {treatmentBill.reasonIfMore ? (
                        <p className="bhc-muted" style={{ marginTop: 8 }}>
                          <strong>Why different from agreed:</strong> {treatmentBill.reasonIfMore}
                        </p>
                      ) : null}
                      {treatmentBill.patientApprovalStatus === "pending" && (
                        <>
                          <p className="bhc-muted" style={{ marginTop: 8 }}>
                            Please confirm you understand these charges.
                          </p>
                          <textarea
                            className="bhc-note-input"
                            rows={2}
                            placeholder="If you dispute, explain here (required for dispute)"
                            value={patientBillDisputeNote}
                            onChange={(e) => setPatientBillDisputeNote(e.target.value)}
                            style={{ width: "100%", marginTop: 8 }}
                          />
                          <div className="bhc-btn-row" style={{ marginTop: 8 }}>
                            <button
                              type="button"
                              className="bhc-btn bhc-btn-primary"
                              disabled={patientBillSubmitting}
                              onClick={() => handlePatientBillRespond("approve")}
                            >
                              Acknowledge
                            </button>
                            <button
                              type="button"
                              className="bhc-btn bhc-btn-danger"
                              disabled={patientBillSubmitting}
                              onClick={() => handlePatientBillRespond("dispute")}
                            >
                              Dispute
                            </button>
                          </div>
                        </>
                      )}
                      {treatmentBill.patientApprovalStatus === "approved" && (
                        <p className="bhc-muted" style={{ marginTop: 8 }}>
                          You acknowledged this bill.
                        </p>
                      )}
                      {treatmentBill.patientApprovalStatus === "disputed" && (
                        <p className="bhc-muted" style={{ marginTop: 8 }}>
                          You disputed this bill. Our team can review the chat history.
                        </p>
                      )}
                      {treatmentBill.patientApprovalStatus === "not_required" && (
                        <p className="bhc-muted" style={{ marginTop: 8 }}>
                          No extra acknowledgment was required (charges at or below agreed offer).
                        </p>
                      )}
                      {billUiError && isPatientMode && (
                        <p className="bhc-inline-err" style={{ marginTop: 6 }}>
                          {billUiError}
                        </p>
                      )}
                    </div>
                  )}

                  {providerPending && !appointmentPendingApproval && (
                    <p className="bhc-muted">Waiting for doctor/hospital to respond.</p>
                  )}
                </>
              )}

              {isProviderMode && (
                <>
                  {providerPending && (
                    <>
                      <div className="bhc-btn-row">
                      <button
                        type="button"
                        className="bhc-btn bhc-btn-primary"
                        onClick={handleAcceptProvider}
                      >
                        Accept
                      </button>

                      <div className="bhc-reverse-inline">
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          min="1"
                          value={reverseBidAmount}
                          onChange={(e) => {
                            const raw = e.target.value;
                            if (bidAmountRawHasDisallowedSymbols(raw)) {
                              setReverseBidError(BID_AMOUNT_CONTACT_BLOCKED_MSG);
                              return;
                            }
                            setReverseBidError("");
                            setReverseBidAmount(formatIndianBidAmountInput(raw));
                          }}
                          onKeyDown={(e) => {
                            if (["e", "E", "+", "-", "."].includes(e.key)) e.preventDefault();
                          }}
                          placeholder="Reverse bid amount"
                        />
                        <button
                          type="button"
                          className="bhc-btn bhc-btn-outline"
                          onClick={handleReverseBid}
                          disabled={reverseBidSubmitting}
                        >
                          Reverse Bid
                        </button>
                        {reverseBidError && <span className="bhc-inline-err">{reverseBidError}</span>}
                      </div>

                      <button
                        type="button"
                        className="bhc-btn bhc-btn-danger"
                        onClick={handleReject}
                      >
                        Reject
                      </button>
                    </div>
                      <p className="bhc-muted bhc-your-move-hint">
                        <strong>Your move:</strong> accept the patient&apos;s budget as listed in the offer summary
                        above, enter a lower <strong>reverse bid</strong> amount and tap Reverse Bid, or tap Reject to
                        decline this request.
                      </p>
                    </>
                  )}

                  {(isProviderAccepted || (isProviderCountered && patientAction !== "pending")) &&
                    !appointment && (
                      <p className="bhc-muted bhc-tbill-context">
                        Bid agreed. Patient can book appointment from their chat.
                      </p>
                    )}

                  {appointment && appointmentPendingApproval && appointmentDoctorAction === "pending" && (
                    <div className="bhc-appointment-card bhc-appointment-card--respond">
                      <p className="bhc-booking-title">Appointment request</p>
                      <div className="bhc-visit-detail">
                        <p className="bhc-visit-detail__line">
                          <strong>When:</strong> {appointmentTimePart(appointment)} on{" "}
                          {appointment.appointmentDate
                            ? new Date(appointment.appointmentDate).toLocaleDateString("en-IN")
                            : "—"}
                        </p>
                        {appointmentClinicLabel(appointment) ? (
                          <p className="bhc-visit-detail__line bhc-visit-detail__line--facility">
                            <span className="bhc-facility-chip" aria-hidden>
                              <ApptIconMapPin />
                            </span>
                            <span className="bhc-visit-detail__line-facility-text">
                              <span className="bhc-visit-detail__facility-label">Clinic / location</span>
                              <span className="bhc-booking-clinic">{appointmentClinicLabel(appointment)}</span>
                            </span>
                          </p>
                        ) : null}
                        {appointment.noteForDoctor ? (
                          <p className="bhc-visit-detail__line">
                            <strong>Note:</strong> {appointment.noteForDoctor}
                          </p>
                        ) : null}
                      </div>

                      <div className="bhc-appt-respond">
                        <button
                          type="button"
                          className="bhc-btn bhc-btn-primary bhc-appt-respond__accept"
                          onClick={handleAppointmentAccept}
                          disabled={!!appointmentSaving}
                        >
                          {appointmentSaving === "accept" ? "Accepting…" : "Accept"}
                        </button>

                        <div className="bhc-appt-reschedule">
                          <p className="bhc-appt-reschedule__hint">
                            Or propose another time (requires admin approval). Prefer a slot from your published
                            availability below.
                          </p>
                          {bookingSlotsLoading ? (
                            <p className="bhc-muted" style={{ marginBottom: 8 }}>
                              Loading your open slots…
                            </p>
                          ) : bookingSlots.length > 0 ? (
                            <div className="bhc-booking" style={{ marginTop: 8 }}>
                              {groupedBookingSlots.map(([dateStr, slots]) => (
                                <div key={dateStr} className="bhc-booking-date-group">
                                  <div className="bhc-booking-row">
                                    <label>{formatBookingDateLabel(dateStr)}</label>
                                    <div className="bhc-slots">
                                      {slots.map((s) => {
                                        const key = `${s.appointmentDate}|${s.timeSlot}`;
                                        const selected =
                                          appointmentRescheduleDate === s.appointmentDate &&
                                          appointmentRescheduleSlot === s.timeSlot;
                                        const clinic = slotClinicLabel(s);
                                        return (
                                          <button
                                            key={key}
                                            type="button"
                                            className={`bhc-slot ${selected ? "active" : ""}`}
                                            onClick={() => {
                                              setAppointmentRescheduleDate(s.appointmentDate);
                                              setAppointmentRescheduleSlot(s.timeSlot);
                                            }}
                                          >
                                            <span className="bhc-slot__time">{slotTimePart(s)}</span>
                                            {clinic ? <span className="bhc-slot__clinic">{clinic}</span> : null}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="bhc-muted" style={{ marginBottom: 8 }}>
                              No published openings in the next two weeks — add availability in Manage Availability, or
                              use manual time below.
                            </p>
                          )}
                          <div className="bhc-appt-reschedule__row">
                            <label className="bhc-appt-field">
                              <span className="bhc-appt-field__label">Date (manual)</span>
                              <input
                                type="date"
                                className="bhc-appt-field__control"
                                value={appointmentRescheduleDate}
                                onChange={(e) => setAppointmentRescheduleDate(e.target.value)}
                              />
                            </label>
                            <label className="bhc-appt-field">
                              <span className="bhc-appt-field__label">Time (manual)</span>
                              <select
                                className="bhc-appt-field__control"
                                value={appointmentRescheduleSlot}
                                onChange={(e) => setAppointmentRescheduleSlot(e.target.value)}
                              >
                                <option value="">Select time</option>
                                {providerRescheduleSlotOptions.map((t) => (
                                  <option key={t} value={t}>
                                    {t}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <button
                              type="button"
                              className="bhc-btn bhc-btn-outline bhc-appt-reschedule__btn"
                              onClick={handleAppointmentReschedule}
                              disabled={
                                !!appointmentSaving ||
                                !appointmentRescheduleDate ||
                                !appointmentRescheduleSlot
                              }
                            >
                              {appointmentSaving === "reschedule" ? "Sending…" : "Reschedule"}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {appointment &&
                    appointmentPendingApproval &&
                    appointmentDoctorAction !== "pending" && (
                      <div className="bhc-admin-wait-banner" role="status">
                        <span className="bhc-admin-wait-banner__icon" aria-hidden>
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M12 3l7 4v5c0 5-3.5 9-7 10-3.5-1-7-5-7-10V7l7-4z"
                              stroke="currentColor"
                              strokeWidth="1.6"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M9 12l2 2 4-5"
                              stroke="currentColor"
                              strokeWidth="1.6"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                        <div className="bhc-admin-wait-banner__body">
                          <p className="bhc-admin-wait-banner__title">Awaiting admin confirmation</p>
                          <p className="bhc-admin-wait-banner__text">
                            Your update is saved. The patient only sees the final visit time after HealBid admin
                            approves it.
                          </p>
                        </div>
                      </div>
                    )}

                  {appointment && appointmentScheduled && (
                    <div className="bhc-appointment-card bhc-appointment-card--confirmed">
                      <p className="bhc-booking-title">Appointment confirmed</p>
                      <div className="bhc-visit-detail">
                        <p className="bhc-visit-detail__line">
                          <strong>When:</strong> {appointmentTimePart(appointment)} on{" "}
                          {appointment.appointmentDate
                            ? new Date(appointment.appointmentDate).toLocaleDateString("en-IN")
                            : "—"}
                        </p>
                        {appointmentClinicLabel(appointment) ? (
                          <p className="bhc-visit-detail__line bhc-visit-detail__line--facility">
                            <span className="bhc-facility-chip" aria-hidden>
                              <ApptIconMapPin />
                            </span>
                            <span className="bhc-visit-detail__line-facility-text">
                              <span className="bhc-visit-detail__facility-label">Clinic / location</span>
                              <span className="bhc-booking-clinic">{appointmentClinicLabel(appointment)}</span>
                            </span>
                          </p>
                        ) : null}
                      </div>
                      <div className="bhc-appt-respond" style={{ marginTop: 8 }}>
                        <button
                          type="button"
                          className="bhc-btn bhc-btn-primary bhc-appt-respond__accept"
                          onClick={handleAppointmentComplete}
                          disabled={!!appointmentSaving}
                        >
                          {appointmentSaving === "complete" ? "Saving…" : "Mark completed"}
                        </button>
                      </div>
                      <div className="bhc-appt-reschedule bhc-appt-reschedule--after">
                        <p className="bhc-booking-title bhc-appt-reschedule__title">Propose a new time</p>
                        <p className="bhc-appt-reschedule__hint">
                          Admin must approve before the patient is notified. Pick a published slot when possible.
                        </p>
                        {bookingSlotsLoading ? (
                          <p className="bhc-muted" style={{ marginBottom: 8 }}>
                            Loading your open slots…
                          </p>
                        ) : bookingSlots.length > 0 ? (
                          <div className="bhc-booking" style={{ marginTop: 8 }}>
                            {groupedBookingSlots.map(([dateStr, slots]) => (
                              <div key={dateStr} className="bhc-booking-date-group">
                                <div className="bhc-booking-row">
                                  <label>{formatBookingDateLabel(dateStr)}</label>
                                  <div className="bhc-slots">
                                    {slots.map((s) => {
                                      const key = `${s.appointmentDate}|${s.timeSlot}`;
                                      const selected =
                                        appointmentRescheduleDate === s.appointmentDate &&
                                        appointmentRescheduleSlot === s.timeSlot;
                                      const clinic = slotClinicLabel(s);
                                      return (
                                        <button
                                          key={key}
                                          type="button"
                                          className={`bhc-slot ${selected ? "active" : ""}`}
                                          onClick={() => {
                                            setAppointmentRescheduleDate(s.appointmentDate);
                                            setAppointmentRescheduleSlot(s.timeSlot);
                                          }}
                                        >
                                          <span className="bhc-slot__time">{slotTimePart(s)}</span>
                                          {clinic ? <span className="bhc-slot__clinic">{clinic}</span> : null}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="bhc-muted" style={{ marginBottom: 8 }}>
                            No published openings — use manual time below or update availability.
                          </p>
                        )}
                        <div className="bhc-appt-reschedule__row">
                          <label className="bhc-appt-field">
                            <span className="bhc-appt-field__label">Date (manual)</span>
                            <input
                              type="date"
                              className="bhc-appt-field__control"
                              value={appointmentRescheduleDate}
                              onChange={(e) => setAppointmentRescheduleDate(e.target.value)}
                            />
                          </label>
                          <label className="bhc-appt-field">
                            <span className="bhc-appt-field__label">Time (manual)</span>
                            <select
                              className="bhc-appt-field__control"
                              value={appointmentRescheduleSlot}
                              onChange={(e) => setAppointmentRescheduleSlot(e.target.value)}
                            >
                              <option value="">Select time</option>
                              {providerRescheduleSlotOptions.map((t) => (
                                <option key={t} value={t}>
                                  {t}
                                </option>
                              ))}
                            </select>
                          </label>
                          <button
                            type="button"
                            className="bhc-btn bhc-btn-outline bhc-appt-reschedule__btn"
                            onClick={handleAppointmentReschedule}
                            disabled={
                              !!appointmentSaving ||
                              !appointmentRescheduleDate ||
                              !appointmentRescheduleSlot
                            }
                          >
                            {appointmentSaving === "reschedule" ? "Sending…" : "Reschedule"}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {bidAgreed && (
                    <div
                      ref={treatmentBillDisclosureRef}
                      className="bhc-appointment-card bhc-tbill-disclosure"
                      style={{ marginTop: 12 }}
                    >
                      <button
                        type="button"
                        className="bhc-tbill-disclosure__toggle"
                        onClick={() => setTreatmentBillSectionOpen((v) => !v)}
                        aria-expanded={treatmentBillSectionOpen}
                        aria-controls={treatmentBillPanelId}
                      >
                        <span className="bhc-tbill-disclosure__toggle-main">
                          <span className="bhc-tbill-disclosure__eyebrow">After visit</span>
                          <span className="bhc-tbill-disclosure__title-row">
                            <span className="bhc-tbill-disclosure__title">Treatment bill</span>
                            {treatmentBill && !treatmentBillSectionOpen ? (
                              <span className="bhc-tbill-disclosure__saved-dot" title="Bill on file">
                                Saved
                              </span>
                            ) : null}
                          </span>
                          <span className="bhc-tbill-disclosure__title-sub">
                            Record at facility · no online payment
                          </span>
                        </span>
                        <span className="bhc-tbill-disclosure__chev-wrap" aria-hidden>
                          <span
                            className={`bhc-tbill-disclosure__chev${treatmentBillSectionOpen ? " is-open" : ""}`}
                          />
                        </span>
                      </button>
                      {billUiError && isProviderMode && !treatmentBillSectionOpen && (
                        <p className="bhc-inline-err bhc-tbill-disclosure__err-collapsed">{billUiError}</p>
                      )}
                      {treatmentBillSectionOpen && (
                        <div
                          className="bhc-tbill-disclosure__panel"
                          role="region"
                          id={treatmentBillPanelId}
                          aria-label="Treatment bill — record charges"
                        >
                          <div className="bhc-tbill-disclosure__panel-head">
                            <div className="bhc-tbill-disclosure__panel-head-main">
                              <span className="bhc-tbill-disclosure__panel-label">Agreed offer</span>
                              <span className="bhc-tbill-disclosure__panel-amount">
                                {agreedPrice != null ? fmtINR(agreedPrice) : "—"}
                              </span>
                            </div>
                            {treatmentBill ? (
                              <span className="bhc-tbill-disclosure__panel-status">
                                On file · patient {treatmentBill.patientApprovalStatus}
                              </span>
                            ) : (
                              <span className="bhc-tbill-disclosure__panel-status bhc-tbill-disclosure__panel-status--muted">
                                Not recorded yet
                              </span>
                            )}
                          </div>
                          {isProviderMode && !appointmentCompleted && (
                            <p className="bhc-tbill-disclosure__callout">
                              Mark appointment as <strong>completed</strong> before submitting the bill.
                            </p>
                          )}
                          <div className="bhc-tbill-disclosure__fields">
                            <div className="bhc-booking-row bhc-tbill-disclosure__field">
                              <label htmlFor={`${treatmentBillFormId}-final`}>Final amount charged (₹)</label>
                              <input
                                id={`${treatmentBillFormId}-final`}
                                type="text"
                                inputMode="decimal"
                                className="bhc-note-input bhc-tbill-disclosure__input"
                                value={billFinalAmount}
                                onChange={(e) => {
                                  setBillFinalAmount(e.target.value);
                                  setBillUiError("");
                                }}
                                placeholder="Amount patient paid at facility"
                              />
                            </div>
                            <div className="bhc-booking-row bhc-tbill-disclosure__field">
                              <label htmlFor={`${treatmentBillFormId}-pay`}>Payment method</label>
                              <select
                                id={`${treatmentBillFormId}-pay`}
                                className="bhc-note-input bhc-tbill-disclosure__input"
                                value={billPaymentMethod}
                                onChange={(e) => setBillPaymentMethod(e.target.value)}
                              >
                                <option value="cash">Cash</option>
                                <option value="card">Card</option>
                                <option value="online">Online (bank / portal)</option>
                                <option value="upi">UPI</option>
                                <option value="other">Other</option>
                              </select>
                            </div>
                            <label className="bhc-tbill-disclosure__check">
                              <input
                                type="checkbox"
                                checked={billChargedMore}
                                onChange={(e) => {
                                  setBillChargedMore(e.target.checked);
                                  setBillUiError("");
                                }}
                              />
                              <span>Patient was charged more than the agreed offer</span>
                            </label>
                            <div className="bhc-booking-row bhc-tbill-disclosure__field">
                              <label htmlFor={`${treatmentBillFormId}-why`}>
                                Why <span className="bhc-tbill-disclosure__label-hint">(if above agreed or checked)</span>
                              </label>
                              <textarea
                                id={`${treatmentBillFormId}-why`}
                                className="bhc-note-input bhc-tbill-disclosure__input"
                                rows={2}
                                value={billReason}
                                onChange={(e) => {
                                  setBillReason(e.target.value);
                                  setBillUiError("");
                                }}
                                placeholder="e.g. extra procedure, implants, pharmacy"
                              />
                            </div>
                            <div className="bhc-booking-row bhc-tbill-disclosure__field">
                              <label htmlFor={`${treatmentBillFormId}-notes`}>Notes for records (optional)</label>
                              <textarea
                                id={`${treatmentBillFormId}-notes`}
                                className="bhc-note-input bhc-tbill-disclosure__input"
                                rows={2}
                                value={billNotes}
                                onChange={(e) => setBillNotes(e.target.value)}
                              />
                            </div>
                          </div>
                          {treatmentBill && (
                            <p className="bhc-tbill-disclosure__meta">
                              Patient acknowledgment: <strong>{treatmentBill.patientApprovalStatus}</strong>
                            </p>
                          )}
                          {billUiError && isProviderMode && (
                            <p className="bhc-inline-err bhc-tbill-disclosure__panel-err">{billUiError}</p>
                          )}
                          <div className="bhc-btn-row bhc-tbill-disclosure__actions">
                            <button
                              type="button"
                              className="bhc-btn bhc-btn-primary bhc-tbill-disclosure__submit"
                              onClick={handleSubmitTreatmentBill}
                              disabled={billSubmitting || (isProviderMode && !appointmentCompleted)}
                            >
                              {billSubmitting ? "Saving…" : "Save treatment bill"}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {bidAgreed && isProviderMode && caseClose ? (
                    <div className="bhc-appointment-card" style={{ marginTop: 12 }}>
                      <p className="bhc-booking-title">Clear from your lists</p>
                      {caseClose.treatmentCompletedAt ? (
                        <p className="bhc-muted" style={{ margin: 0 }}>
                          This case is closed and no longer appears on Patient Bids or Confirmed Patients.
                        </p>
                      ) : caseClose.canClose ? (
                        <>
                          <p className="bhc-muted" style={{ margin: "0 0 10px" }}>
                            After the visit outcome is recorded and the treatment bill is settled in this chat, confirm
                            here. Until then, this patient stays on your active lists.
                          </p>
                          <button
                            type="button"
                            className="bhc-btn bhc-btn-primary"
                            onClick={handleVerifyCloseCase}
                            disabled={verifyCloseSubmitting}
                          >
                            {verifyCloseSubmitting ? "Closing…" : "Verify billing complete & remove from my lists"}
                          </button>
                        </>
                      ) : (
                        <>
                          <p className="bhc-muted" style={{ margin: "0 0 6px" }}>
                            To archive this case from your desk:
                          </p>
                          <ul style={{ margin: "0 0 0 18px", padding: 0 }}>
                            {(caseClose.reasons || []).map((r) => (
                              <li key={r} className="bhc-muted" style={{ marginBottom: 4 }}>
                                {r}
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </div>
                  ) : null}
                </>
              )}

              {appointment && appointmentCompleted && (
                <div className="bhc-appointment-card">
                  <p className="bhc-booking-title">Appointment completed</p>
                  <div className="bhc-visit-detail">
                    <p className="bhc-muted" style={{ margin: "0 0 6px" }}>
                      <strong>When:</strong> {appointmentTimePart(appointment)} on{" "}
                      {appointment.appointmentDate
                        ? new Date(appointment.appointmentDate).toLocaleDateString()
                        : "—"}
                    </p>
                    {appointmentClinicLabel(appointment) ? (
                      <p className="bhc-muted" style={{ margin: 0 }}>
                        <strong>Clinic / location:</strong>{" "}
                        <span className="bhc-booking-clinic">{appointmentClinicLabel(appointment)}</span>
                      </p>
                    ) : null}
                  </div>
                </div>
              )}
              </div>
            </div>
          </>
        )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalTree, document.body);
}
